// FILE: src/pages/ShiftsPage.jsx
//
// Shifts page — UI redesign.
//
// Goals of the redesign:
//   - Geist / Geist Mono typography
//   - Scoped CSS under `.shpg-root` (so it can't bleed into other pages)
//   - Sticky page-top (title, action bar, filter bar)
//   - 12-column table with grouped headers (Till count / Cash to Bank)
//   - Status chips (Pending / Checked) + soft-red tint for till-error rows
//   - 5 modals (Add, Add Actual, View, Edit, Revert) re-skinned but powered
//     by MUI Dialog/Autocomplete/Snackbar underneath for accessibility.
//
// IMPORTANT — what we preserved from the previous file:
//   - URL state for all filters (account, c2bStatus, dates, search, tillVariance)
//     + pagination (page, limit). Defaults stay out of the URL.
//   - requestIdRef race-condition guard on the list fetch.
//   - All 5 modal flows (Add / Add Actual / View / Edit / Revert) with their
//     handlers exactly as they were — only their visuals changed.
//   - Lock-aware error handling on Edit/Revert (admins/accountants can revert
//     a Checked shift; other roles see the disabled hint).
//   - Batch Create Shifts navigation, Refresh button, Snackbar.
//   - Auto-calculated cashInTotal / cashOutTotal / tillVariance / tillError /
//     expectedC2B that update as the user types (these are form-state, NOT
//     aggregated stats, so they stay client-side).
//
// Note on stats: the design shows a "pending count" chip in the header and on
// the Add Actual button. Per the user constraint ("don't do stats client side
// if we don't have them on server") we fetch this count via a dedicated server
// query — `getShifts({ c2bStatus: 'Pending', limit: 1 }).totalCount` — instead
// of computing it from whatever happens to be on the current page.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Autocomplete,
  MenuItem,
  Menu,
  CircularProgress,
  Checkbox,
  Tooltip,
} from "@mui/material";

import { useNavigate, useSearchParams } from "react-router-dom";

import API from "../api";
import { formatDate } from "../utils/formatDate";
import DenominationCounter, {
  makeDefaultDenominationLines,
} from "../components/DenominationCounter";

import {
  createShift,
  getShifts,
  finalizeShifts,
  getAllUsers,
  getShiftById,
} from "../api";
import MyPaginationComponent from "../components/MyPaginationComponent";

// ----------------------------------------
// Numeric input helper (kept as-is)
// ----------------------------------------
const disableScrollProps = {
  onWheel: (e) => e.target.blur(),
  step: "any",
  style: { MozAppearance: "textfield" },
};

// ----------------------------------------
// URL query-string contract.
// Defaults stay out of the URL so it's clean when the user hasn't filtered.
// ----------------------------------------
const SHIFT_LIST_DEFAULT_LIMIT = 50;
const SHIFT_LIST_ALLOWED_LIMITS = [10, 25, 50, 100, 200];
const SHIFT_LIST_ALLOWED_C2B_STATUS = ["Pending", "Checked"];
const SHIFT_LIST_ALLOWED_TILL_VARIANCE = ["nonzero"];

function parseShiftListParams(searchParams) {
  const rawPage = parseInt(searchParams.get("page"), 10);
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;

  const rawLimit = parseInt(searchParams.get("limit"), 10);
  const limit = SHIFT_LIST_ALLOWED_LIMITS.includes(rawLimit)
    ? rawLimit
    : SHIFT_LIST_DEFAULT_LIMIT;

  const c2bRaw = searchParams.get("c2bStatus") || "";
  const c2bStatusFilter = SHIFT_LIST_ALLOWED_C2B_STATUS.includes(c2bRaw)
    ? c2bRaw
    : "";

  const tvRaw = searchParams.get("tillVariance") || "";
  const tillVarianceFilter = SHIFT_LIST_ALLOWED_TILL_VARIANCE.includes(tvRaw)
    ? tvRaw
    : "";

  return {
    page,
    limit,
    accountFilter: searchParams.get("accountId") || "",
    c2bStatusFilter,
    startDateFilter: searchParams.get("startDate") || "",
    endDateFilter: searchParams.get("endDate") || "",
    searchText: searchParams.get("search") || "",
    tillVarianceFilter,
  };
}

// ----------------------------------------
// Small money formatter — UK locale.
// (We re-use formatDate from the project for dates.)
// ----------------------------------------
// Local (browser-timezone) dates as "YYYY-MM-DD" for <input type="date">.
// Deliberately NOT toISOString() — that would give the UTC date, which is
// yesterday/tomorrow near midnight for non-UTC users.
function localDateISO(d) {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}
function localTodayISO() {
  return localDateISO(new Date());
}
function localYesterdayISO() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return localDateISO(d);
}

function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toFixed(2);
}
function fmtMoneySigned(n) {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (Math.abs(num) < 0.005) return "0.00";
  const sign = num > 0 ? "+" : "−";
  return `${sign}${Math.abs(num).toFixed(2)}`;
}

// The server stores only NON-ZERO denomination rows. When hydrating the Edit
// counter, merge them into the full denomination set so every denomination
// keeps an input row (compare in pence to dodge float identity issues); any
// non-standard stored denomination is appended so nothing is ever dropped.
function mergeIntoDefaultLines(storedLines) {
  const merged = makeDefaultDenominationLines();
  const extras = [];
  for (const l of storedLines) {
    const pence = Math.round(Number(l.denomination) * 100);
    const hit = merged.find((m) => Math.round(m.denomination * 100) === pence);
    if (hit) hit.quantity = Number(l.quantity) || 0;
    else
      extras.push({
        denomination: Number(l.denomination),
        quantity: Number(l.quantity) || 0,
      });
  }
  return [...merged, ...extras];
}

// Sum denomination lines in integer pence (matches the server's math) to
// avoid float drift in the declared-cash counted total.
function sumDenomLines(lines) {
  return (
    (lines || []).reduce(
      (s, l) =>
        s + Math.round(Number(l.denomination) * 100) * (Number(l.quantity) || 0),
      0
    ) / 100
  );
}

// ----------------------------------------
// CSS — fully scoped under `.shpg-root`.
// ----------------------------------------
const SHIFTS_PAGE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;450;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

/* Dialog papers reuse .shpg-root for tokens — never inherit page sizing */
.MuiPaper-root.shpg-root {
  min-height: 0;
  background: #fff;
}
.shpg-root {
  --shpg-primary: #1E90FF;
  --shpg-primary-soft: #E8F3FF;
  --shpg-warn: #FFC107;
  --shpg-warn-soft: #FFF7DC;
  --shpg-warn-deep: #B86E00;
  --shpg-success: #2E7D32;
  --shpg-success-soft: #E8F5E9;
  --shpg-danger: #C62828;
  --shpg-danger-soft: #FDECEC;
  --shpg-text-1: #111827;
  --shpg-text-2: #374151;
  --shpg-text-3: #6B7280;
  --shpg-text-4: #9CA3AF;
  --shpg-border-1: #ECECEC;
  --shpg-border-2: #DDDDDD;
  --shpg-border-strong: #C9CDD3;
  --shpg-surface: #FFFFFF;
  --shpg-surface-sub: #FAFAFA;
  --shpg-surface-sub-2: #F4F4F5;
  --shpg-radius-sm: 6px;
  --shpg-radius-md: 8px;
  --shpg-radius-lg: 12px;
  --shpg-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --shpg-shadow-md: 0 4px 16px rgba(15, 23, 42, 0.06);
  --shpg-navbar-h: 60px;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'cv11', 'ss03';
  color: var(--shpg-text-1);
  background: var(--shpg-surface-sub);
  min-height: 100vh;
  padding: 0;
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: -0.005em;
}
.shpg-root * { box-sizing: border-box; }

/* ----- Sticky top region (page header + filter bar) ----- */
.shpg-sticky-top {
  position: sticky;
  top: var(--shpg-navbar-h);
  z-index: 30;
  background: var(--shpg-surface-sub);
  border-bottom: 1px solid var(--shpg-border-1);
}
.shpg-header {
  padding: 20px 24px 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.shpg-title-block { min-width: 0; flex: 1 1 320px; }
.shpg-title {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0 0 4px;
  color: var(--shpg-text-1);
}
.shpg-subtitle {
  display: flex; align-items: center; gap: 10px;
  font-size: 12.5px; color: var(--shpg-text-3); flex-wrap: wrap;
}
.shpg-subtitle-pill {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--shpg-warn-soft);
  color: var(--shpg-warn-deep);
  padding: 3px 9px; border-radius: 999px;
  font-size: 11.5px; font-weight: 500;
  border: 1px solid #F1D88A;
}
.shpg-subtitle-pill b { font-weight: 600; }
.shpg-subtitle-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--shpg-text-4); }
.shpg-refreshed {
  display: inline-flex; align-items: center; gap: 5px;
  font-family: 'Geist Mono', monospace; font-size: 11px;
  color: var(--shpg-text-3);
}
.shpg-refreshed-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--shpg-success); animation: shpg-pulse 2s infinite;
}
@keyframes shpg-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

/* ----- Action bar ----- */
.shpg-actions {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.shpg-btn {
  display: inline-flex; align-items: center; gap: 7px;
  height: 34px; padding: 0 14px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  border-radius: var(--shpg-radius-md);
  border: 1px solid transparent; cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.shpg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.shpg-btn-primary { background: var(--shpg-primary); color: #fff; }
.shpg-btn-primary:hover:not(:disabled) { background: #1A7DD9; }
.shpg-btn-warn { background: var(--shpg-warn); color: #1F1A0A; }
.shpg-btn-warn:hover:not(:disabled) { background: #E6AD00; }
.shpg-btn-success { background: var(--shpg-success); color: #fff; }
.shpg-btn-success:hover:not(:disabled) { background: #266B2A; }
.shpg-btn-danger-soft { background: var(--shpg-danger-soft); color: var(--shpg-danger); border-color: #F4C7C7; }
.shpg-btn-danger-soft:hover:not(:disabled) { background: #FBDBDB; }
.shpg-btn-ghost {
  background: var(--shpg-surface); color: var(--shpg-text-2);
  border-color: var(--shpg-border-2);
}
.shpg-btn-ghost:hover:not(:disabled) { background: var(--shpg-surface-sub-2); }
.shpg-btn-icon {
  width: 34px; padding: 0; justify-content: center;
}
.shpg-btn-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  background: rgba(0,0,0,0.15); color: inherit;
  border-radius: 9px; font-size: 10.5px; font-weight: 600;
  font-family: 'Geist Mono', monospace;
}
.shpg-btn-primary .shpg-btn-count { background: rgba(255,255,255,0.25); }
.shpg-btn-warn .shpg-btn-count { background: rgba(0,0,0,0.2); }
.shpg-workflow-hint {
  font-size: 11.5px; color: var(--shpg-text-3);
  margin-left: 4px;
  display: inline-flex; align-items: center; gap: 4px;
}
.shpg-workflow-hint b { font-weight: 600; color: var(--shpg-text-2); }

/* ----- Filter bar ----- */
.shpg-filterbar {
  display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap;
  padding: 14px 24px 16px;
  background: var(--shpg-surface);
  border-top: 1px solid var(--shpg-border-1);
}
.shpg-filter-field { display: flex; flex-direction: column; gap: 4px; }
.shpg-filter-field label {
  font-size: 10.5px; font-weight: 500; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--shpg-text-3);
}
.shpg-input, .shpg-select {
  height: 34px; padding: 0 10px;
  background: var(--shpg-surface);
  border: 1px solid var(--shpg-border-2);
  border-radius: var(--shpg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--shpg-text-1);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  min-width: 140px;
}
.shpg-input:focus, .shpg-select:focus {
  outline: none;
  border-color: var(--shpg-primary);
  box-shadow: 0 0 0 3px var(--shpg-primary-soft);
}
.shpg-input[type="date"] { font-family: 'Geist Mono', monospace; font-size: 12.5px; min-width: 140px; }
.shpg-input-search {
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg' fill='none'%3E%3Ccircle cx='7' cy='7' r='4.5' stroke='%236B7280' stroke-width='1.4'/%3E%3Cpath d='M10.5 10.5l3 3' stroke='%236B7280' stroke-width='1.4' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 10px center;
  padding-left: 30px;
  min-width: 200px;
}
.shpg-filter-actions { display: flex; align-items: center; gap: 6px; margin-left: auto; }
.shpg-link-btn {
  background: none; border: none; padding: 0;
  color: var(--shpg-primary); font-size: 12px; cursor: pointer; font-family: inherit;
  text-decoration: underline; text-underline-offset: 2px;
}
.shpg-link-btn:hover { color: #1A7DD9; }

/* ----- Body region ----- */
.shpg-body { padding: 18px 24px 32px; }
.shpg-alert {
  margin-bottom: 14px;
  padding: 10px 14px;
  background: var(--shpg-danger-soft);
  color: var(--shpg-danger);
  border-left: 3px solid var(--shpg-danger);
  border-radius: var(--shpg-radius-sm);
  font-size: 13px;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.shpg-alert button {
  background: none; border: none; color: inherit; cursor: pointer;
  font-size: 16px; line-height: 1; padding: 0 4px;
}

/* ----- Table card ----- */
.shpg-card {
  background: var(--shpg-surface);
  border: 1px solid var(--shpg-border-1);
  border-radius: var(--shpg-radius-lg);
  box-shadow: var(--shpg-shadow-sm);
  overflow: hidden;
}
.shpg-table-wrap { width: 100%; overflow-x: auto; }
.shpg-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  min-width: 1260px;
}
.shpg-table thead tr.shpg-group-row th {
  background: var(--shpg-surface-sub-2);
  color: var(--shpg-text-3);
  font-size: 10.5px; font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 6px 10px;
  border-bottom: 1px solid var(--shpg-border-1);
  text-align: left;
}
.shpg-table thead tr.shpg-group-row th.shpg-g-till {
  background: linear-gradient(0deg, rgba(255,193,7,0.06), rgba(255,193,7,0.06)), var(--shpg-surface-sub-2);
  color: var(--shpg-warn-deep);
}
.shpg-table thead tr.shpg-group-row th.shpg-g-c2b {
  background: linear-gradient(0deg, rgba(30,144,255,0.06), rgba(30,144,255,0.06)), var(--shpg-surface-sub-2);
  color: #0E5EAB;
}
.shpg-table thead tr.shpg-head-row th {
  background: var(--shpg-surface);
  color: var(--shpg-text-2);
  font-size: 11.5px; font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 10px 10px;
  border-bottom: 1px solid var(--shpg-border-2);
  text-align: left;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 2;
}
.shpg-table thead tr.shpg-head-row th.r { text-align: right; }
.shpg-table tbody td {
  padding: 11px 10px;
  border-bottom: 1px solid var(--shpg-border-1);
  vertical-align: middle;
  color: var(--shpg-text-1);
}
.shpg-table tbody td.r {
  text-align: right;
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
}
.shpg-table tbody td.mono {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 12.5px;
}
.shpg-table tbody tr {
  cursor: pointer;
  transition: background 0.1s ease;
}
.shpg-table tbody tr:hover { background: var(--shpg-surface-sub); }
.shpg-table tbody tr.shpg-row-pending {
  box-shadow: inset 3px 0 0 var(--shpg-warn);
}
.shpg-table tbody tr.shpg-row-error {
  background: linear-gradient(0deg, rgba(198,40,40,0.04), rgba(198,40,40,0.04));
}
.shpg-table tbody tr.shpg-row-error:hover {
  background: linear-gradient(0deg, rgba(198,40,40,0.07), rgba(198,40,40,0.07));
}

.shpg-acc-cell {
  display: flex; flex-direction: column; gap: 1px; min-width: 140px;
}
.shpg-acc-name { font-weight: 500; color: var(--shpg-text-1); font-size: 12.5px; }
.shpg-acc-no {
  font-family: 'Geist Mono', monospace;
  font-size: 10.5px; color: var(--shpg-text-3);
}
.shpg-till-name { font-size: 12.5px; color: var(--shpg-text-2); }

.shpg-val-pos { color: var(--shpg-success); font-weight: 500; }
.shpg-val-neg { color: var(--shpg-danger); font-weight: 500; }
.shpg-val-zero { color: var(--shpg-text-3); }
.shpg-val-pending { color: var(--shpg-text-4); font-style: italic; font-family: 'Geist', sans-serif !important; }

/* Status chip */
.shpg-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px 2px 6px;
  font-size: 11px; font-weight: 500;
  border-radius: 999px;
  white-space: nowrap;
}
.shpg-chip-dot { width: 6px; height: 6px; border-radius: 50%; }
.shpg-chip-pending {
  background: var(--shpg-warn-soft);
  color: var(--shpg-warn-deep);
  border: 1px solid #F1D88A;
}
.shpg-chip-pending .shpg-chip-dot { background: var(--shpg-warn-deep); }
.shpg-chip-checked {
  background: var(--shpg-success-soft);
  color: var(--shpg-success);
  border: 1px solid #B7DBBA;
}
.shpg-chip-checked .shpg-chip-dot { background: var(--shpg-success); }

/* Row actions menu trigger */
.shpg-row-actions {
  display: flex; justify-content: flex-end; align-items: center; gap: 6px;
}
.shpg-icon-btn {
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--shpg-radius-sm);
  color: var(--shpg-text-3);
  cursor: pointer;
  transition: all 0.12s ease;
}
.shpg-icon-btn:hover {
  background: var(--shpg-surface-sub-2);
  color: var(--shpg-text-1);
  border-color: var(--shpg-border-2);
}

/* ----- Empty / Loading / Error states ----- */
.shpg-state {
  padding: 56px 32px;
  text-align: center;
  color: var(--shpg-text-3);
}
.shpg-state-icon {
  margin: 0 auto 12px;
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: var(--shpg-surface-sub-2);
  color: var(--shpg-text-3);
}
.shpg-state-title { color: var(--shpg-text-2); font-size: 14px; font-weight: 500; margin-bottom: 4px; }
.shpg-state-sub { font-size: 12.5px; color: var(--shpg-text-3); max-width: 360px; margin: 0 auto; }

/* ----- Pagination wrapper ----- */
.shpg-pagination-wrap {
  padding: 8px 12px;
  border-top: 1px solid var(--shpg-border-1);
  background: var(--shpg-surface);
}

/* ----- Modal skinning (MUI Dialog underneath, custom look) ----- */
.shpg-dlg .MuiPaper-root {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif !important;
  border-radius: var(--shpg-radius-lg) !important;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18) !important;
  overflow: hidden;
}
.shpg-dlg-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--shpg-border-1);
}
.shpg-dlg-title { font-size: 16px; font-weight: 600; color: var(--shpg-text-1); margin: 0; }
.shpg-dlg-sub { font-size: 12px; color: var(--shpg-text-3); margin-top: 3px; }
.shpg-dlg-close {
  background: none; border: none; color: var(--shpg-text-3);
  width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.shpg-dlg-close:hover { background: var(--shpg-surface-sub-2); color: var(--shpg-text-1); }

.shpg-dlg-body { padding: 18px 20px; }
.shpg-dlg-body.shpg-no-pad { padding: 0; }
.shpg-dlg-foot {
  padding: 14px 20px;
  border-top: 1px solid var(--shpg-border-1);
  background: var(--shpg-surface-sub);
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px;
}
.shpg-dlg-foot-l { font-size: 12px; color: var(--shpg-text-3); }
.shpg-dlg-foot-r { display: flex; gap: 8px; }

/* Form field bits inside dialogs */
.shpg-flab {
  display: block; font-size: 11px; font-weight: 500;
  color: var(--shpg-text-2); letter-spacing: 0.02em;
  text-transform: uppercase; margin-bottom: 4px;
}
.shpg-flab-req::after { content: ' *'; color: var(--shpg-primary); font-weight: 700; }
.shpg-fhint { font-size: 11px; color: var(--shpg-text-3); margin-top: 3px; }
.shpg-frow { display: grid; gap: 12px; margin-bottom: 14px; }
.shpg-frow-2 { grid-template-columns: 1fr 1fr; }
.shpg-frow-3 { grid-template-columns: 1fr 1fr 1fr; }
.shpg-frow-4 { grid-template-columns: 1fr 1fr 1fr 1fr; }
@media (max-width: 760px) {
  .shpg-frow-2, .shpg-frow-3, .shpg-frow-4 { grid-template-columns: 1fr; }
}

/* Section group inside dialogs */
.shpg-fsec {
  margin-bottom: 18px;
  padding: 14px;
  background: var(--shpg-surface-sub);
  border: 1px solid var(--shpg-border-1);
  border-radius: var(--shpg-radius-md);
}
.shpg-fsec-head {
  display: flex; align-items: center; gap: 8px;
  font-size: 12.5px; font-weight: 600;
  color: var(--shpg-text-1);
  margin-bottom: 12px;
}
.shpg-fsec-head-num {
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 4px;
  background: var(--shpg-primary); color: #fff;
  font-size: 11px; font-weight: 700;
}
.shpg-fsec-hint {
  margin-left: auto;
  font-size: 11px; font-weight: 400; color: var(--shpg-text-3);
}

/* Money input — visually shows the £ */
.shpg-money-input {
  position: relative; display: flex; align-items: center;
}
.shpg-money-input::before {
  content: '£';
  position: absolute; left: 10px;
  font-family: 'Geist Mono', monospace;
  color: var(--shpg-text-3); font-size: 12.5px;
  pointer-events: none;
}
.shpg-money-input input {
  width: 100%; height: 34px;
  padding-left: 22px; padding-right: 10px;
  border: 1px solid var(--shpg-border-2);
  border-radius: var(--shpg-radius-md);
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  color: var(--shpg-text-1);
  background: #fff;
}
.shpg-money-input input:focus {
  outline: none; border-color: var(--shpg-primary);
  box-shadow: 0 0 0 3px var(--shpg-primary-soft);
}

.shpg-finput {
  width: 100%; height: 34px; padding: 0 10px;
  border: 1px solid var(--shpg-border-2);
  border-radius: var(--shpg-radius-md);
  font-family: inherit; font-size: 13px;
  color: var(--shpg-text-1); background: #fff;
}
.shpg-finput:focus {
  outline: none; border-color: var(--shpg-primary);
  box-shadow: 0 0 0 3px var(--shpg-primary-soft);
}
.shpg-finput-mono { font-family: 'Geist Mono', monospace; font-size: 12.5px; }
.shpg-ftextarea {
  width: 100%; min-height: 64px; padding: 8px 10px; resize: vertical;
  border: 1px solid var(--shpg-border-2); border-radius: var(--shpg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--shpg-text-1);
  background: #fff;
}
.shpg-ftextarea:focus {
  outline: none; border-color: var(--shpg-primary);
  box-shadow: 0 0 0 3px var(--shpg-primary-soft);
}

/* Cash-in / Cash-out line items */
.shpg-lines { display: flex; flex-direction: column; gap: 6px; }
.shpg-line { display: grid; grid-template-columns: 140px 1fr 30px; gap: 8px; align-items: center; }
.shpg-line-rm {
  width: 28px; height: 28px;
  background: none; border: 1px solid transparent;
  color: var(--shpg-text-4); cursor: pointer;
  border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
}
.shpg-line-rm:hover { color: var(--shpg-danger); border-color: #F4C7C7; background: var(--shpg-danger-soft); }
.shpg-line-add {
  align-self: flex-start;
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: 1px dashed var(--shpg-border-strong);
  color: var(--shpg-text-2); padding: 6px 12px;
  border-radius: var(--shpg-radius-md);
  font-family: inherit; font-size: 12px; cursor: pointer;
  margin-top: 4px;
}
.shpg-line-add:hover { color: var(--shpg-primary); border-color: var(--shpg-primary); }
.shpg-lines-total {
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 8px; padding-top: 8px;
  border-top: 1px dashed var(--shpg-border-2);
  font-size: 12px; color: var(--shpg-text-2);
}
.shpg-lines-total b {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-weight: 600; color: var(--shpg-text-1);
}

/* Add Shift split layout (form + live calc rail) */
.shpg-shift-body { display: grid; grid-template-columns: 1fr 280px; gap: 0; }
@media (max-width: 980px) { .shpg-shift-body { grid-template-columns: 1fr; } }
.shpg-shift-form { padding: 18px 20px; max-height: 70vh; overflow-y: auto; }
.shpg-shift-side {
  padding: 18px 20px;
  background: linear-gradient(180deg, var(--shpg-surface-sub) 0%, #fff 100%);
  border-left: 1px solid var(--shpg-border-1);
  display: flex; flex-direction: column; gap: 14px;
  position: sticky; top: 0; align-self: start;
  max-height: 70vh; overflow-y: auto;
}
@media (max-width: 980px) {
  .shpg-shift-side { border-left: 0; border-top: 1px solid var(--shpg-border-1); position: static; max-height: none; }
}
.shpg-shift-side-head {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 12px; font-weight: 600; color: var(--shpg-text-2);
  padding-bottom: 10px; border-bottom: 1px solid var(--shpg-border-1);
}
.shpg-shift-side-pill {
  font-size: 10px; font-weight: 500;
  color: var(--shpg-primary); background: var(--shpg-primary-soft);
  padding: 2px 8px; border-radius: 999px;
}
.shpg-calc { display: flex; flex-direction: column; gap: 10px; }
.shpg-calc-row {
  display: flex; flex-direction: column; gap: 3px;
  padding: 8px 10px;
  background: var(--shpg-surface);
  border: 1px solid var(--shpg-border-1);
  border-radius: var(--shpg-radius-sm);
}
.shpg-calc-row.is-feature {
  background: linear-gradient(0deg, rgba(30,144,255,0.04), rgba(30,144,255,0.04)), #fff;
  border-color: rgba(30,144,255,0.15);
}
.shpg-calc-lab {
  font-size: 10.5px; font-weight: 500;
  color: var(--shpg-text-3); text-transform: uppercase; letter-spacing: 0.04em;
}
.shpg-calc-val {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 15px; font-weight: 600;
  color: var(--shpg-text-1);
}
.shpg-calc-val.is-pos { color: var(--shpg-success); }
.shpg-calc-val.is-neg { color: var(--shpg-danger); }
.shpg-calc-formula {
  font-family: 'Geist Mono', monospace;
  font-size: 10px; color: var(--shpg-text-4); margin-top: 1px;
}

/* Add Actual modal table */
.shpg-pending-table {
  width: 100%; border-collapse: separate; border-spacing: 0;
  font-size: 12.5px;
}
.shpg-pending-table th {
  background: var(--shpg-surface-sub);
  color: var(--shpg-text-2);
  font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.04em; padding: 9px 8px;
  border-bottom: 1px solid var(--shpg-border-2);
  text-align: left;
}
.shpg-pending-table th.r { text-align: right; }
.shpg-pending-table th.c-check { width: 32px; }
.shpg-pending-table td {
  padding: 8px;
  border-bottom: 1px solid var(--shpg-border-1);
  vertical-align: middle;
}
.shpg-pending-table tr.is-sel { background: rgba(30,144,255,0.03); }
.shpg-pending-mini-input,
.shpg-pending-mini-text {
  width: 100%; height: 30px; padding: 0 8px;
  border: 1px solid var(--shpg-border-2);
  border-radius: var(--shpg-radius-sm);
  font-family: 'Geist Mono', monospace; font-size: 12.5px;
}
.shpg-pending-mini-text { font-family: 'Geist', sans-serif; }
.shpg-pending-mini-input:disabled, .shpg-pending-mini-text:disabled {
  background: var(--shpg-surface-sub); color: var(--shpg-text-4);
}
.shpg-pending-mini-input:focus, .shpg-pending-mini-text:focus {
  outline: none; border-color: var(--shpg-primary);
  box-shadow: 0 0 0 2px var(--shpg-primary-soft);
}
.shpg-pending-totals {
  margin-top: 12px; padding: 10px 12px;
  background: var(--shpg-primary-soft);
  border-radius: var(--shpg-radius-md);
  display: flex; align-items: center; justify-content: flex-end; gap: 16px;
  font-size: 12.5px;
}
.shpg-pending-totals b {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-weight: 600; color: #0E5EAB;
}

/* "Set the date first" guidance — banner + pulsing date field */
.shpg-required-banner {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 12px;
  background: var(--shpg-warn-soft);
  border: 1px solid #F1D88A;
  border-left: 3px solid var(--shpg-warn);
  border-radius: var(--shpg-radius-md);
  margin: 4px 0 12px;
  font-size: 12.5px;
  color: var(--shpg-warn-deep);
  line-height: 1.45;
}
.shpg-required-banner svg { flex-shrink: 0; margin-top: 1px; }
.shpg-required-banner b { font-weight: 600; }
.shpg-finput.is-required-empty,
.shpg-finput.is-required-empty:focus {
  border-color: var(--shpg-warn) !important;
  background: #FFFDF5;
}
.shpg-finput.is-required-empty {
  animation: shpg-pulse-warn 1.8s ease-in-out infinite;
}
.shpg-finput.is-required-empty.is-flashing {
  animation: shpg-flash-warn 0.55s ease-out 3;
}
@keyframes shpg-pulse-warn {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); }
  50% { box-shadow: 0 0 0 4px rgba(255, 193, 7, 0.25); }
}
@keyframes shpg-flash-warn {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); }
  50% { box-shadow: 0 0 0 6px rgba(255, 193, 7, 0.55); }
}
/* Wrapper that lets clicks reach our onClick even when the inner element is
   disabled (native disabled controls swallow events). */
.shpg-disabled-wrap {
  display: inline-flex; align-items: center;
  cursor: not-allowed;
}
.shpg-disabled-wrap.is-active { cursor: pointer; }

/* View Details modal styling */
.shpg-detail-hero {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; padding: 14px 16px;
  background: var(--shpg-surface-sub);
  border-radius: var(--shpg-radius-md);
  margin-bottom: 16px;
}
.shpg-detail-hero-date {
  font-size: 12px; color: var(--shpg-text-3); margin-bottom: 2px;
}
.shpg-detail-hero-acc { font-size: 15px; font-weight: 600; color: var(--shpg-text-1); }
.shpg-detail-hero-r { text-align: right; }
.shpg-detail-hero-lab { font-size: 10.5px; text-transform: uppercase; color: var(--shpg-text-3); letter-spacing: 0.04em; }
.shpg-detail-hero-val {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 22px; font-weight: 600; color: var(--shpg-text-1);
}
.shpg-detail-sec { margin-bottom: 18px; }
.shpg-detail-sec-head {
  font-size: 11.5px; font-weight: 600;
  color: var(--shpg-text-2); text-transform: uppercase; letter-spacing: 0.04em;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--shpg-border-1);
}
.shpg-detail-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
}
.shpg-detail-grid-2 { grid-template-columns: repeat(2, 1fr); }
@media (max-width: 600px) { .shpg-detail-grid, .shpg-detail-grid-2 { grid-template-columns: 1fr 1fr; } }
.shpg-detail-field-lab {
  font-size: 10.5px; text-transform: uppercase; color: var(--shpg-text-3);
  letter-spacing: 0.04em; margin-bottom: 2px;
}
.shpg-detail-field-val {
  font-family: 'Geist Mono', monospace; font-variant-numeric: tabular-nums;
  font-size: 13px; font-weight: 500; color: var(--shpg-text-1);
}
.shpg-detail-field-val.is-text { font-family: 'Geist', sans-serif; font-weight: 400; }
.shpg-detail-field-val.is-empty { color: var(--shpg-text-4); font-style: italic; }
.shpg-detail-field-val.is-pos { color: var(--shpg-success); }
.shpg-detail-field-val.is-neg { color: var(--shpg-danger); }
.shpg-detail-lines { display: flex; flex-direction: column; gap: 4px; }
.shpg-detail-line {
  display: flex; justify-content: space-between; align-items: center;
  padding: 6px 10px;
  background: var(--shpg-surface-sub);
  border-radius: var(--shpg-radius-sm);
  font-size: 12.5px;
}
.shpg-detail-line-desc { color: var(--shpg-text-2); }
.shpg-detail-line-amt {
  font-family: 'Geist Mono', monospace; font-variant-numeric: tabular-nums;
  font-weight: 500; color: var(--shpg-text-1);
}
.shpg-detail-empty {
  font-size: 12.5px; color: var(--shpg-text-4); font-style: italic;
  padding: 8px 10px; text-align: center;
}
.shpg-detail-notes {
  padding: 10px 12px;
  background: var(--shpg-warn-soft);
  border-left: 3px solid var(--shpg-warn);
  border-radius: var(--shpg-radius-sm);
  font-size: 12.5px; color: var(--shpg-text-2); line-height: 1.5;
}
.shpg-detail-people {
  display: flex; flex-wrap: wrap; gap: 6px;
}
.shpg-people-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px;
  background: var(--shpg-surface-sub-2);
  border: 1px solid var(--shpg-border-1);
  border-radius: 999px;
  font-size: 11.5px; color: var(--shpg-text-2);
}
.shpg-detail-meta {
  display: flex; gap: 14px; flex-wrap: wrap;
  margin-top: 12px; padding-top: 12px;
  border-top: 1px solid var(--shpg-border-1);
  font-size: 11px; color: var(--shpg-text-3);
}
.shpg-detail-meta b { color: var(--shpg-text-2); font-weight: 500; }

/* Revert modal */
.shpg-revert-context {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 12px;
  background: var(--shpg-surface-sub);
  border-radius: var(--shpg-radius-md);
  margin-bottom: 12px;
}
.shpg-revert-context-l { display: flex; flex-direction: column; gap: 2px; }
.shpg-revert-context-lab { font-size: 10.5px; text-transform: uppercase; color: var(--shpg-text-3); letter-spacing: 0.04em; }
.shpg-revert-context-val { font-size: 13px; font-weight: 500; color: var(--shpg-text-1); }
.shpg-revert-warning {
  display: flex; gap: 10px; padding: 11px 12px;
  background: var(--shpg-warn-soft);
  border: 1px solid #F1D88A;
  border-radius: var(--shpg-radius-md);
  margin-bottom: 14px;
}
.shpg-revert-warning-text { font-size: 12.5px; color: var(--shpg-warn-deep); line-height: 1.5; }

/* MUI Autocomplete polish inside our dialogs */
.shpg-mui-ac .MuiOutlinedInput-root {
  font-family: 'Geist', sans-serif !important;
  font-size: 13px !important;
  padding: 2px 8px !important;
  min-height: 34px !important;
  background: #fff;
}
.shpg-mui-ac .MuiOutlinedInput-notchedOutline {
  border-color: var(--shpg-border-2) !important;
  border-radius: var(--shpg-radius-md) !important;
}
.shpg-mui-ac.Mui-focused .MuiOutlinedInput-notchedOutline {
  border-color: var(--shpg-primary) !important;
}
.shpg-mui-ac .MuiAutocomplete-tag {
  font-family: 'Geist', sans-serif !important;
  height: 22px !important;
  font-size: 11.5px !important;
}

/* Mini spinner */
.shpg-spinner {
  width: 14px; height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: shpg-spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes shpg-spin { to { transform: rotate(360deg); } }
`;

// ----------------------------------------
// Small inline icons
// ----------------------------------------
function IconPlus({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function IconRefresh({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M13.5 8a5.5 5.5 0 11-1.7-3.98M13.5 3v3h-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconUpload({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 11V3M5 6l3-3 3 3M3 13h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconSearch({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10.5 10.5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconClose({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconDots({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <circle cx="3" cy="8" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="13" cy="8" r="1.4" />
    </svg>
  );
}

// ----------------------------------------
// Reusable: status chip
// ----------------------------------------
function StatusChip({ status }) {
  const s = (status || "").toLowerCase();
  if (s === "checked") {
    return (
      <span className="shpg-chip shpg-chip-checked">
        <span className="shpg-chip-dot" />
        Checked
      </span>
    );
  }
  return (
    <span className="shpg-chip shpg-chip-pending">
      <span className="shpg-chip-dot" />
      Pending
    </span>
  );
}

// ----------------------------------------
// Reusable: signed value display
// ----------------------------------------
function SignedVal({ value }) {
  if (value === null || value === undefined || value === "") {
    return <span className="shpg-val-pending">—</span>;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) return <span className="shpg-val-pending">—</span>;
  const cls = n > 0.005 ? "shpg-val-pos" : n < -0.005 ? "shpg-val-neg" : "shpg-val-zero";
  return <span className={cls}>{fmtMoneySigned(n)}</span>;
}

// ===============================================================
// MAIN PAGE
// ===============================================================
export default function ShiftsPage() {
  // ----------------------------------------
  // LIST state
  // ----------------------------------------
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshedAt, setRefreshedAt] = useState(null);

  // URL-backed filters + pagination
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    page,
    limit,
    accountFilter,
    c2bStatusFilter,
    startDateFilter,
    endDateFilter,
    searchText,
    tillVarianceFilter,
  } = parseShiftListParams(searchParams);

  const [totalCount, setTotalCount] = useState(0);

  // Default date window: yesterday → today, seeded into the URL on a FRESH
  // visit only (no params). Bookmarked/filtered URLs are respected, and
  // "Clear all" mid-session still shows the unfiltered list.
  const didSeedDefaultDatesRef = useRef(false);
  useEffect(() => {
    if (didSeedDefaultDatesRef.current) return;
    didSeedDefaultDatesRef.current = true;
    if ([...searchParams.keys()].length > 0) return;
    const seeded = new URLSearchParams();
    seeded.set("startDate", localYesterdayISO());
    seeded.set("endDate", localTodayISO());
    setSearchParams(seeded, { replace: true });
  }, [searchParams, setSearchParams]);

  // Server-driven pending count (don't fake it on client)
  const [pendingTotalCount, setPendingTotalCount] = useState(null);

  // Race-condition guards
  const requestIdRef = useRef(0);
  const pendingCountReqRef = useRef(0);

  // Reference data
  const [accounts, setAccounts] = useState([]);
  const [c2bCategories, setC2bCategories] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [tillUsers, setTillUsers] = useState([]);
  // Logged-in user's id — used to default the Shift Manager on Add Shift.
  const [currentUserId, setCurrentUserId] = useState(null);
  // Display currency for the declared-cash counter (from /users/me).
  const [userCurrency, setUserCurrency] = useState("USD");

  // Current user role (for revert + finalize visibility)
  const userRole = localStorage.getItem("userRole") || "";
  const isManager = userRole.toLowerCase() === "manager";

  // ----------------------------------------
  // ADD SHIFT dialog state
  // ----------------------------------------
  const [openAddShift, setOpenAddShift] = useState(false);
  const [creatingShift, setCreatingShift] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    account: "",
    date: "",
    tillCategory: "",
    startingFloat: "",
    cashSales: "",
    driversWage: "",
    cashInLines: [{ amount: "", description: "" }],
    cashOutLines: [{ amount: "", description: "" }],
    targetCash: "",
    declaredCash: "",
    tillUsers: [],
    shiftManager: "",
    tillNotes: "",
  });
  const [calcVals, setCalcVals] = useState({
    cashInTotal: 0,
    cashOutTotal: 0,
    tillVariance: 0,
    tillError: 0,
    expectedC2B: 0,
  });
  // Declared-cash entry mode: null = direct entry, array = counting lines.
  const [shiftCountLines, setShiftCountLines] = useState(null);

  // ----------------------------------------
  // FINALIZE (Add Actual) state
  // ----------------------------------------
  const [openFinalize, setOpenFinalize] = useState(false);
  const [finalizeAccount, setFinalizeAccount] = useState("");
  const [pendingShifts, setPendingShifts] = useState([]);
  const [actualC2BDate, setActualC2BDate] = useState("");
  const [finalizeMap, setFinalizeMap] = useState({});
  const [isFinalizing, setIsFinalizing] = useState(false);
  // Ref so we can pull the user's attention to the date input when they tap
  // a disabled row checkbox / Actual C2B input without setting the date first.
  const actualC2BDateRef = useRef(null);
  // Brief highlight pulse when the user clicks something blocked by an empty date
  const [dateFlashCount, setDateFlashCount] = useState(0);
  const focusActualC2BDate = useCallback(() => {
    setDateFlashCount((c) => c + 1);
    if (actualC2BDateRef.current) {
      try {
        actualC2BDateRef.current.focus();
        if (typeof actualC2BDateRef.current.showPicker === "function") {
          actualC2BDateRef.current.showPicker();
        }
      } catch (_e) {
        // Some browsers throw if showPicker is called without a user gesture
        // chain; the focus() above is enough to draw the eye there.
      }
    }
  }, []);

  // ----------------------------------------
  // DETAIL + EDIT state
  // ----------------------------------------
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);

  const [openEdit, setOpenEdit] = useState(false);
  const [editShiftData, setEditShiftData] = useState(null);
  const [editCalcVals, setEditCalcVals] = useState({
    cashInTotal: 0,
    cashOutTotal: 0,
    tillVariance: 0,
    tillError: 0,
    expectedC2B: 0,
  });
  // Declared-cash counting state for the Edit dialog. editHadCount remembers
  // whether the shift ALREADY had a stored breakdown, so switching back to
  // direct entry knows to send [] (delete) rather than omit the field.
  const [editCountLines, setEditCountLines] = useState(null);
  const [editHadCount, setEditHadCount] = useState(false);

  // ----------------------------------------
  // REVERT modal state
  // ----------------------------------------
  const [openRevertModal, setOpenRevertModal] = useState(false);
  const [selectedShiftForRevert, setSelectedShiftForRevert] = useState(null);
  const [revertReason, setRevertReason] = useState("");
  const [reverting, setReverting] = useState(false);

  // ----------------------------------------
  // Row action menu (per-row)
  // ----------------------------------------
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuShift, setActionMenuShift] = useState(null);

  // ----------------------------------------
  // Snackbar
  // ----------------------------------------
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const handleCloseSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  // ----------------------------------------
  // Reference data fetch (once on mount)
  // ----------------------------------------
  useEffect(() => {
    fetchAccounts();
    fetchC2BCategories();
    fetchAllSystemUsers();
    fetchTillUsers();
    fetchCurrentUser();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await API.get("/accounts?limit=100000");
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error("fetchAccounts Error:", err);
    }
  };

  const fetchC2BCategories = async () => {
    try {
      const res = await API.get("/categories?limit=100000");
      const filtered = (res.data || []).filter((cat) => cat.type === "C2B");
      setC2bCategories(filtered);
    } catch (err) {
      console.error("fetchC2BCategories Error:", err);
    }
  };

  const fetchAllSystemUsers = async () => {
    try {
      const users = await getAllUsers();
      setAllUsers(users);
    } catch (err) {
      console.error("fetchAllSystemUsers error:", err);
    }
  };

  const fetchTillUsers = async () => {
    try {
      const res = await API.get("/tilluser?limit=100000");
      setTillUsers(res.data.tillUsers || []);
    } catch (err) {
      console.error("fetchTillUsers error:", err);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const res = await API.get("/users/me");
      setCurrentUserId(res.data?._id || null);
      setUserCurrency(res.data?.currency || "USD");
    } catch (err) {
      console.error("fetchCurrentUser error:", err);
    }
  };

  // ----------------------------------------
  // Pending-only count (server-driven)
  // ----------------------------------------
  const fetchPendingCount = useCallback(async () => {
    const myId = ++pendingCountReqRef.current;
    try {
      const data = await getShifts({ c2bStatus: "Pending", limit: 1 });
      if (myId !== pendingCountReqRef.current) return;
      setPendingTotalCount(Number(data.totalCount || 0));
    } catch (err) {
      if (myId !== pendingCountReqRef.current) return;
      console.error("fetchPendingCount error:", err);
      // Keep previous value rather than misleading the user with "0"
    }
  }, []);

  // ----------------------------------------
  // List fetch
  // ----------------------------------------
  const fetchShifts = useCallback(async () => {
    const myRequestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    try {
      const params = { page, limit };
      if (accountFilter) params.account = accountFilter;
      if (c2bStatusFilter) params.c2bStatus = c2bStatusFilter;
      if (startDateFilter) params.startDate = startDateFilter;
      if (endDateFilter) params.endDate = endDateFilter;
      if (searchText) params.search = searchText;
      if (tillVarianceFilter) params.tillVarianceFilter = tillVarianceFilter;

      const data = await getShifts(params);
      if (myRequestId !== requestIdRef.current) return;
      setShifts(data.data || []);
      setTotalCount(data.totalCount || 0);
      setRefreshedAt(new Date());
    } catch (err) {
      if (myRequestId !== requestIdRef.current) return;
      console.error("Error fetching shifts:", err);
      setError(err.response?.data?.message || "Failed to load shifts");
    } finally {
      if (myRequestId === requestIdRef.current) setLoading(false);
    }
  }, [
    page,
    limit,
    accountFilter,
    c2bStatusFilter,
    startDateFilter,
    endDateFilter,
    searchText,
    tillVarianceFilter,
  ]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  // Refresh the pending count whenever the list refreshes (cheap query).
  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount, refreshedAt]);

  // ----------------------------------------
  // ADD SHIFT handlers (unchanged logic)
  // ----------------------------------------
  const handleOpenAddShift = () => {
    // Default the manager to whoever is creating the shift — but only when
    // their role can actually be picked as a manager (Staff stays empty).
    const canBeManager = ["admin", "manager", "accountant"].includes(
      userRole.toLowerCase()
    );
    setShiftForm({
      account: "",
      date: localTodayISO(),
      tillCategory: "",
      startingFloat: "",
      cashSales: "",
      driversWage: "",
      cashInLines: [{ amount: "", description: "" }],
      cashOutLines: [{ amount: "", description: "" }],
      targetCash: "",
      declaredCash: "",
      tillUsers: [],
      shiftManager: canBeManager && currentUserId ? currentUserId : "",
      tillNotes: "",
    });
    setCalcVals({
      cashInTotal: 0,
      cashOutTotal: 0,
      tillVariance: 0,
      tillError: 0,
      expectedC2B: 0,
    });
    setShiftCountLines(null);
    setOpenAddShift(true);
  };
  const handleCloseAddShift = () => setOpenAddShift(false);

  const handleAddCashInLine = () =>
    setShiftForm((prev) => ({
      ...prev,
      cashInLines: [...prev.cashInLines, { amount: "", description: "" }],
    }));
  const handleRemoveCashInLine = (index) =>
    setShiftForm((prev) => {
      const arr = [...prev.cashInLines];
      arr.splice(index, 1);
      return { ...prev, cashInLines: arr };
    });
  const handleCashInLineChange = (index, field, value) =>
    setShiftForm((prev) => {
      const arr = [...prev.cashInLines];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, cashInLines: arr };
    });
  const handleAddCashOutLine = () =>
    setShiftForm((prev) => ({
      ...prev,
      cashOutLines: [...prev.cashOutLines, { amount: "", description: "" }],
    }));
  const handleRemoveCashOutLine = (index) =>
    setShiftForm((prev) => {
      const arr = [...prev.cashOutLines];
      arr.splice(index, 1);
      return { ...prev, cashOutLines: arr };
    });
  const handleCashOutLineChange = (index, field, value) =>
    setShiftForm((prev) => {
      const arr = [...prev.cashOutLines];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, cashOutLines: arr };
    });

  // Live calc (Add Shift)
  useEffect(() => {
    let totalIn = 0;
    shiftForm.cashInLines.forEach((l) => (totalIn += Number(l.amount || 0)));
    let totalOut = 0;
    shiftForm.cashOutLines.forEach((l) => (totalOut += Number(l.amount || 0)));

    const startF = Number(shiftForm.startingFloat || 0);
    const cSales = Number(shiftForm.cashSales || 0);
    const dWage = Number(shiftForm.driversWage || 0);
    const tCash = Number(shiftForm.targetCash || 0);
    const dCash = Number(shiftForm.declaredCash || 0);

    const tillVariance = dCash - tCash;
    const tillError = tCash - (startF + cSales + totalIn - totalOut - dWage);
    let expectedC2B = dCash - startF;
    if (expectedC2B < 0) expectedC2B = 0;

    setCalcVals({
      cashInTotal: totalIn,
      cashOutTotal: totalOut,
      tillVariance,
      tillError,
      expectedC2B,
    });
  }, [shiftForm]);

  // While counting, Declared cash stays locked to the counted total.
  useEffect(() => {
    if (shiftCountLines !== null) {
      setShiftForm((p) => ({
        ...p,
        declaredCash: sumDenomLines(shiftCountLines),
      }));
    }
  }, [shiftCountLines]);

  const sanitizeQty = (value) =>
    value === "" ? "" : Math.max(0, Math.floor(Number(value) || 0));
  const handleAddCountChange = (index, value) =>
    setShiftCountLines((prev) =>
      prev.map((l, i) =>
        i === index ? { ...l, quantity: sanitizeQty(value) } : l
      )
    );
  const handleEditCountChange = (index, value) =>
    setEditCountLines((prev) =>
      prev.map((l, i) =>
        i === index ? { ...l, quantity: sanitizeQty(value) } : l
      )
    );

  const handleCreateShift = async () => {
    setCreatingShift(true);
    try {
      const payload = {
        ...shiftForm,
        date: shiftForm.date ? new Date(shiftForm.date) : null,
        startingFloat: Number(shiftForm.startingFloat || 0),
        cashSales: Number(shiftForm.cashSales || 0),
        driversWage: Number(shiftForm.driversWage || 0),
        targetCash: Number(shiftForm.targetCash || 0),
        declaredCash: Number(shiftForm.declaredCash || 0),
        cashInLines: shiftForm.cashInLines.map((l) => ({
          amount: Number(l.amount || 0),
          description: l.description || "",
        })),
        cashOutLines: shiftForm.cashOutLines.map((l) => ({
          amount: Number(l.amount || 0),
          description: l.description || "",
        })),
        tillUsers: shiftForm.tillUsers,
        shiftManager: shiftForm.shiftManager,
        ...(shiftCountLines !== null
          ? {
              declaredCashDenominations: shiftCountLines.map((l) => ({
                denomination: l.denomination,
                quantity: Number(l.quantity) || 0,
              })),
            }
          : {}),
      };

      await createShift(payload);
      setSnackbar({
        open: true,
        message: "Shift created successfully.",
        severity: "success",
      });
      fetchShifts();
      setOpenAddShift(false);
    } catch (err) {
      console.error("Create SHIFT Error:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to create SHIFT",
        severity: "error",
      });
    } finally {
      setCreatingShift(false);
    }
  };

  // ----------------------------------------
  // FINALIZE (Add Actual) handlers
  // ----------------------------------------
  const handleOpenFinalize = () => {
    setOpenFinalize(true);
    setFinalizeAccount("");
    setPendingShifts([]);
    setActualC2BDate("");
    setFinalizeMap({});
    setDateFlashCount(0);
  };
  const handleCloseFinalize = () => setOpenFinalize(false);

  const handleFinalizeAccountChange = async (val) => {
    setFinalizeAccount(val);
    setPendingShifts([]);
    setFinalizeMap({});
    if (!val) return;
    try {
      const data = await getShifts({
        account: val,
        c2bStatus: "Pending",
        limit: 9999,
        onlyActual: "true",
      });
      const shiftsPending = data.data || [];
      setPendingShifts(shiftsPending);
      const mapObj = {};
      // Auto-default actualC2B to expectedC2B (matches design's UX choice)
      shiftsPending.forEach((sh) => {
        mapObj[sh._id] = {
          checked: false,
          actualC2B:
            typeof sh.expectedC2B === "number"
              ? sh.expectedC2B.toFixed(2)
              : "",
          c2bNotes: "",
        };
      });
      setFinalizeMap(mapObj);
    } catch (err) {
      console.error("Fetch pending SHIFTs error:", err);
    }
  };

  const handleToggleShiftCheck = (shiftId, checked) =>
    setFinalizeMap((prev) => ({
      ...prev,
      [shiftId]: { ...prev[shiftId], checked },
    }));
  const handleActualC2BChange = (shiftId, val) =>
    setFinalizeMap((prev) => ({
      ...prev,
      [shiftId]: { ...prev[shiftId], actualC2B: val },
    }));
  const handleC2BNotesChange = (shiftId, val) =>
    setFinalizeMap((prev) => ({
      ...prev,
      [shiftId]: { ...prev[shiftId], c2bNotes: val },
    }));
  const handleToggleAllPending = () => {
    const allOn = pendingShifts.every((s) => finalizeMap[s._id]?.checked);
    const next = {};
    pendingShifts.forEach((s) => {
      next[s._id] = { ...finalizeMap[s._id], checked: !allOn };
    });
    setFinalizeMap(next);
  };
  const getRowC2BVariance = (shiftId, actualVal) => {
    const shObj = pendingShifts.find((s) => s._id === shiftId);
    if (!shObj) return null;
    return Number(actualVal || 0) - (shObj.expectedC2B || 0);
  };

  const selectedPendingCount = useMemo(
    () => Object.values(finalizeMap).filter((r) => r?.checked).length,
    [finalizeMap]
  );

  const handleFinalizeSave = async () => {
    setIsFinalizing(true);
    try {
      const shiftsToFinalize = pendingShifts.filter(
        (shift) => finalizeMap[shift._id]?.checked
      );
      if (shiftsToFinalize.length === 0) {
        setSnackbar({
          open: true,
          message: "Please select at least one shift to finalize.",
          severity: "warning",
        });
        setIsFinalizing(false);
        return;
      }

      const payloadArray = shiftsToFinalize.map((shift) => ({
        shiftId: shift._id,
        actualC2B: Number(finalizeMap[shift._id].actualC2B || 0),
        c2bNotes: finalizeMap[shift._id].c2bNotes,
      }));

      await finalizeShifts({
        account: finalizeAccount,
        actualC2BDate,
        shiftsData: payloadArray,
      });

      setSnackbar({
        open: true,
        message: "Shifts finalized successfully.",
        severity: "success",
      });
      fetchShifts();
      setOpenFinalize(false);
    } catch (err) {
      console.error("Finalize shifts error:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to finalize shifts",
        severity: "error",
      });
    } finally {
      setIsFinalizing(false);
    }
  };

  // ----------------------------------------
  // REVERT handlers
  // ----------------------------------------
  const handleOpenRevertModal = (shift) => {
    setSelectedShiftForRevert(shift);
    setRevertReason("");
    setOpenRevertModal(true);
  };
  const handleCloseRevertModal = () => {
    setOpenRevertModal(false);
    setSelectedShiftForRevert(null);
    setRevertReason("");
  };
  const handleConfirmRevert = async () => {
    if (!revertReason.trim()) return;
    setReverting(true);
    try {
      const res = await API.post(
        `/shifts/revert/${selectedShiftForRevert._id}`,
        { reason: revertReason.trim() }
      );
      setSnackbar({
        open: true,
        message: res.data.message || "Shift reverted successfully.",
        severity: "success",
      });
      fetchShifts();
      handleCloseRevertModal();
    } catch (err) {
      console.error("Error reverting shift:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to revert shift",
        severity: "error",
      });
    } finally {
      setReverting(false);
    }
  };

  // ----------------------------------------
  // EDIT handlers
  // ----------------------------------------
  const handleEditChange = (eOrObj) => {
    // Accepts either a synthetic event OR a {name, value} object
    const name = eOrObj.target ? eOrObj.target.name : eOrObj.name;
    const value = eOrObj.target ? eOrObj.target.value : eOrObj.value;
    setEditShiftData((prev) => ({ ...prev, [name]: value }));
  };
  const handleEditCashInLineChange = (index, field, value) =>
    setEditShiftData((prev) => {
      const updated = [...(prev.cashInLines || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, cashInLines: updated };
    });
  const handleEditAddCashInLine = () =>
    setEditShiftData((prev) => ({
      ...prev,
      cashInLines: [
        ...(prev.cashInLines || []),
        { amount: "", description: "" },
      ],
    }));
  const handleEditRemoveCashInLine = (index) =>
    setEditShiftData((prev) => {
      const updated = [...(prev.cashInLines || [])];
      updated.splice(index, 1);
      return { ...prev, cashInLines: updated };
    });
  const handleEditCashOutLineChange = (index, field, value) =>
    setEditShiftData((prev) => {
      const updated = [...(prev.cashOutLines || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, cashOutLines: updated };
    });
  const handleEditAddCashOutLine = () =>
    setEditShiftData((prev) => ({
      ...prev,
      cashOutLines: [
        ...(prev.cashOutLines || []),
        { amount: "", description: "" },
      ],
    }));
  const handleEditRemoveCashOutLine = (index) =>
    setEditShiftData((prev) => {
      const updated = [...(prev.cashOutLines || [])];
      updated.splice(index, 1);
      return { ...prev, cashOutLines: updated };
    });

  // Live calc (Edit)
  useEffect(() => {
    if (!editShiftData) return;
    let totalIn = 0;
    (editShiftData.cashInLines || []).forEach(
      (l) => (totalIn += Number(l.amount || 0))
    );
    let totalOut = 0;
    (editShiftData.cashOutLines || []).forEach(
      (l) => (totalOut += Number(l.amount || 0))
    );
    const sF = Number(editShiftData.startingFloat || 0);
    const cS = Number(editShiftData.cashSales || 0);
    const dW = Number(editShiftData.driversWage || 0);
    const tC = Number(editShiftData.targetCash || 0);
    const dC = Number(editShiftData.declaredCash || 0);
    const tillVariance = dC - tC;
    const tillError = tC - (sF + cS + totalIn - totalOut - dW);
    let expectedC2B = dC - sF;
    if (expectedC2B < 0) expectedC2B = 0;
    setEditCalcVals({
      cashInTotal: totalIn,
      cashOutTotal: totalOut,
      tillVariance,
      tillError,
      expectedC2B,
    });
  }, [editShiftData]);

  // While counting in the Edit dialog, Declared cash follows the counted total.
  useEffect(() => {
    if (editCountLines !== null) {
      setEditShiftData((p) =>
        p ? { ...p, declaredCash: sumDenomLines(editCountLines) } : p
      );
    }
  }, [editCountLines]);

  const handleSaveEdit = async () => {
    if (!editShiftData) return;
    try {
      const shiftId = editShiftData._id;
      const payload = {
        ...editShiftData,
        date: editShiftData.date ? new Date(editShiftData.date) : null,
        startingFloat: Number(editShiftData.startingFloat || 0),
        cashSales: Number(editShiftData.cashSales || 0),
        driversWage: Number(editShiftData.driversWage || 0),
        targetCash: Number(editShiftData.targetCash || 0),
        declaredCash: Number(editShiftData.declaredCash || 0),
        cashInLines: (editShiftData.cashInLines || []).map((l) => ({
          amount: Number(l.amount || 0),
          description: l.description || "",
        })),
        cashOutLines: (editShiftData.cashOutLines || []).map((l) => ({
          amount: Number(l.amount || 0),
          description: l.description || "",
        })),
        tillUsers: editShiftData.tillUsers || [],
        // Counting → send the lines; switched back to direct on a shift that
        // HAD a breakdown → send [] so the server deletes it; otherwise omit.
        ...(editCountLines !== null
          ? {
              declaredCashDenominations: editCountLines.map((l) => ({
                denomination: l.denomination,
                quantity: Number(l.quantity) || 0,
              })),
            }
          : editHadCount
          ? { declaredCashDenominations: [] }
          : {}),
      };

      const res = await API.put(`/shifts/${shiftId}`, payload);
      setSnackbar({
        open: true,
        message: res.data.message || "Shift updated successfully.",
        severity: "success",
      });
      fetchShifts();
      handleCloseEdit();
    } catch (err) {
      console.error("Edit shift error:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to update shift.",
        severity: "error",
      });
    }
  };
  const handleCloseEdit = () => {
    setEditShiftData(null);
    setEditCountLines(null);
    setEditHadCount(false);
    setOpenEdit(false);
  };

  // ----------------------------------------
  // ROW click → details
  // ----------------------------------------
  const handleRowClick = async (id) => {
    try {
      const detailedShift = await getShiftById(id);
      setSelectedShift(detailedShift);
      setOpenDetail(true);
    } catch (err) {
      console.error("Error fetching shift details:", err);
      setSnackbar({
        open: true,
        message: "Failed to load shift details",
        severity: "error",
      });
    }
  };
  const handleCloseDetail = () => {
    setSelectedShift(null);
    setOpenDetail(false);
  };

  const loadEditShiftData = (shiftObj) => {
    const clone = JSON.parse(JSON.stringify(shiftObj));
    // Managed separately via editCountLines — keep it out of the PUT spread.
    delete clone.declaredCashDenominations;
    if (clone.date) clone.date = new Date(clone.date).toISOString();
    if (clone.tillCategory && typeof clone.tillCategory === "object") {
      clone.tillCategory = clone.tillCategory._id;
    }
    if (Array.isArray(clone.tillUsers) && clone.tillUsers.length > 0) {
      clone.tillUsers = clone.tillUsers.map((u) => u._id);
    } else {
      clone.tillUsers = [];
    }
    if (clone.shiftManager && typeof clone.shiftManager === "object") {
      clone.shiftManager = clone.shiftManager._id;
    }
    setEditShiftData(clone);
    setOpenEdit(true);
  };

  const handleEditShift = async (shift) => {
    if (shift.c2bStatus?.toLowerCase() === "checked") {
      setSnackbar({
        open: true,
        message: "This shift is Checked. Please revert before editing.",
        severity: "warning",
      });
      return;
    }
    // Fetch the detail so the declared-cash breakdown (if any) hydrates too.
    try {
      const detailed = await getShiftById(shift._id);
      const lines = Array.isArray(detailed.declaredCashDenominations)
        ? detailed.declaredCashDenominations
        : [];
      loadEditShiftData(detailed);
      setEditHadCount(lines.length > 0);
      setEditCountLines(lines.length > 0 ? mergeIntoDefaultLines(lines) : null);
    } catch (err) {
      console.error("Error loading shift for edit:", err);
      setSnackbar({
        open: true,
        message: "Failed to load shift for editing",
        severity: "error",
      });
    }
  };

  // ----------------------------------------
  // URL helpers
  // ----------------------------------------
  const updateUrl = useCallback(
    (updates) => {
      const next = { ...parseShiftListParams(searchParams), ...updates };
      const params = new URLSearchParams();
      if (next.accountFilter) params.set("accountId", next.accountFilter);
      if (next.c2bStatusFilter) params.set("c2bStatus", next.c2bStatusFilter);
      if (next.startDateFilter) params.set("startDate", next.startDateFilter);
      if (next.endDateFilter) params.set("endDate", next.endDateFilter);
      if (next.searchText) params.set("search", next.searchText);
      if (next.tillVarianceFilter)
        params.set("tillVariance", next.tillVarianceFilter);
      if (next.page > 1) params.set("page", String(next.page));
      if (next.limit !== SHIFT_LIST_DEFAULT_LIMIT)
        params.set("limit", String(next.limit));
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = () => {
    updateUrl({ page: 1 });
    fetchShifts();
  };
  const handleClearFilters = () => setSearchParams(new URLSearchParams());

  const navigate = useNavigate();

  // Row action menu helpers
  const openRowActions = (e, shift) => {
    e.stopPropagation();
    setActionMenuAnchor(e.currentTarget);
    setActionMenuShift(shift);
  };
  const closeRowActions = () => {
    setActionMenuAnchor(null);
    setActionMenuShift(null);
  };

  const refreshedAtLabel = useMemo(() => {
    if (!refreshedAt) return "";
    const hh = String(refreshedAt.getHours()).padStart(2, "0");
    const mm = String(refreshedAt.getMinutes()).padStart(2, "0");
    const ss = String(refreshedAt.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
  }, [refreshedAt]);

  const hasAnyFilter =
    accountFilter ||
    c2bStatusFilter ||
    startDateFilter ||
    endDateFilter ||
    searchText ||
    tillVarianceFilter;

  // Account & till option resolvers
  const accountOptionByValue = useCallback(
    (val) => accounts.find((a) => a._id === val) || null,
    [accounts]
  );
  const tillOptionByValue = useCallback(
    (val) => c2bCategories.find((c) => c._id === val) || null,
    [c2bCategories]
  );
  const userOptionByValue = useCallback(
    (val) => allUsers.find((u) => u._id === val) || null,
    [allUsers]
  );

  // =================================================
  // RENDER
  // =================================================
  return (
    <div className="shpg-root">
      <style>{SHIFTS_PAGE_CSS}</style>

      {/* Sticky page-top */}
      <div className="shpg-sticky-top">
        <div className="shpg-header">
          <div className="shpg-title-block">
            <h1 className="shpg-title">Shifts</h1>
            <div className="shpg-subtitle">
              {pendingTotalCount !== null && pendingTotalCount > 0 && (
                <span className="shpg-subtitle-pill">
                  <b>{pendingTotalCount}</b>
                  {pendingTotalCount === 1 ? "shift" : "shifts"} awaiting actual C2B
                </span>
              )}
              {refreshedAt && (
                <>
                  {pendingTotalCount !== null && pendingTotalCount > 0 && (
                    <span className="shpg-subtitle-dot" />
                  )}
                  <span className="shpg-refreshed">
                    <span className="shpg-refreshed-dot" />
                    Refreshed {refreshedAtLabel}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Action bar */}
          <div className="shpg-actions">
            <button
              type="button"
              className="shpg-btn shpg-btn-primary"
              onClick={handleOpenAddShift}
            >
              <IconPlus /> Add Shift
            </button>
            {!isManager && (
              <button
                type="button"
                className="shpg-btn shpg-btn-warn"
                onClick={handleOpenFinalize}
              >
                <IconPlus /> Add Actual
                {pendingTotalCount !== null && pendingTotalCount > 0 && (
                  <span className="shpg-btn-count">{pendingTotalCount}</span>
                )}
              </button>
            )}
            <button
              type="button"
              className="shpg-btn shpg-btn-ghost"
              onClick={() => navigate("/shifts/batch-create")}
            >
              <IconUpload /> Batch Create
            </button>
            <button
              type="button"
              className="shpg-btn shpg-btn-ghost shpg-btn-icon"
              onClick={() => {
                fetchShifts();
                fetchPendingCount();
              }}
              aria-label="Refresh"
              title="Refresh"
            >
              <IconRefresh />
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="shpg-filterbar">
          <div className="shpg-filter-field">
            <label htmlFor="shpg-acc">Account</label>
            <select
              id="shpg-acc"
              className="shpg-select"
              value={accountFilter}
              onChange={(e) =>
                updateUrl({ accountFilter: e.target.value, page: 1 })
              }
            >
              <option value="">All accounts</option>
              {accounts.map((acc) => (
                <option key={acc._id} value={acc._id}>
                  {acc.accountName} ({acc.accountNo})
                </option>
              ))}
            </select>
          </div>

          <div className="shpg-filter-field">
            <label htmlFor="shpg-status">Status</label>
            <select
              id="shpg-status"
              className="shpg-select"
              value={c2bStatusFilter}
              onChange={(e) =>
                updateUrl({ c2bStatusFilter: e.target.value, page: 1 })
              }
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Checked">Checked</option>
            </select>
          </div>

          <div className="shpg-filter-field">
            <label htmlFor="shpg-from">From</label>
            <input
              id="shpg-from"
              className="shpg-input"
              type="date"
              value={startDateFilter}
              onChange={(e) =>
                updateUrl({ startDateFilter: e.target.value, page: 1 })
              }
            />
          </div>

          <div className="shpg-filter-field">
            <label htmlFor="shpg-to">To</label>
            <input
              id="shpg-to"
              className="shpg-input"
              type="date"
              value={endDateFilter}
              onChange={(e) =>
                updateUrl({ endDateFilter: e.target.value, page: 1 })
              }
            />
          </div>

          <div className="shpg-filter-field">
            <label htmlFor="shpg-tv">Till variance</label>
            <select
              id="shpg-tv"
              className="shpg-select"
              value={tillVarianceFilter}
              onChange={(e) =>
                updateUrl({ tillVarianceFilter: e.target.value, page: 1 })
              }
            >
              <option value="">Any</option>
              <option value="nonzero">Non-zero only</option>
            </select>
          </div>

          <div className="shpg-filter-field" style={{ flex: "1 1 200px", minWidth: 200 }}>
            <label htmlFor="shpg-search">Search notes</label>
            <input
              id="shpg-search"
              className="shpg-input shpg-input-search"
              type="text"
              placeholder="Search by notes…"
              value={searchText}
              onChange={(e) =>
                updateUrl({ searchText: e.target.value, page: 1 })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
          </div>

          <div className="shpg-filter-actions">
            <button
              type="button"
              className="shpg-btn shpg-btn-primary"
              onClick={handleSearch}
            >
              <IconSearch /> Search
            </button>
            {hasAnyFilter && (
              <button
                type="button"
                className="shpg-link-btn"
                onClick={handleClearFilters}
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="shpg-body">
        {error && (
          <div className="shpg-alert" role="alert">
            <span>{error}</span>
            <button onClick={() => setError("")} aria-label="Dismiss">
              ×
            </button>
          </div>
        )}

        <div className="shpg-card">
          {loading ? (
            <div className="shpg-state">
              <CircularProgress size={28} />
              <div className="shpg-state-title" style={{ marginTop: 12 }}>
                Loading shifts…
              </div>
            </div>
          ) : shifts.length === 0 ? (
            <div className="shpg-state">
              <div className="shpg-state-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 7h18M3 12h18M3 17h12"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="shpg-state-title">No shifts found</div>
              <div className="shpg-state-sub">
                {hasAnyFilter
                  ? "Try clearing the filters above, or add a new shift."
                  : "Add your first shift or use the Batch Create flow to import several at once."}
              </div>
            </div>
          ) : (
            <>
              <div className="shpg-table-wrap">
                <table className="shpg-table">
                  <thead>
                    <tr className="shpg-group-row">
                      <th colSpan={4}>Shift</th>
                      <th colSpan={4} className="shpg-g-till">
                        Till count
                      </th>
                      <th colSpan={3} className="shpg-g-c2b">
                        Cash to Bank
                      </th>
                      <th style={{ width: 60 }} />
                    </tr>
                    <tr className="shpg-head-row">
                      <th>Date</th>
                      <th>Account</th>
                      <th>Till</th>
                      <th>Status</th>
                      <th className="r">Target</th>
                      <th className="r">Declared</th>
                      <th className="r">Variance</th>
                      <th className="r">Error</th>
                      <th className="r">Expected</th>
                      <th className="r">Actual</th>
                      <th className="r">Variance</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.map((sh) => {
                      const isChecked =
                        sh.c2bStatus?.toLowerCase() === "checked";
                      const hasError =
                        typeof sh.tillError === "number" &&
                        Math.abs(sh.tillError) > 0.005;
                      const rowCls = [
                        !isChecked ? "shpg-row-pending" : "",
                        hasError ? "shpg-row-error" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");
                      return (
                        <tr
                          key={sh._id}
                          className={rowCls}
                          onClick={() => handleRowClick(sh._id)}
                        >
                          <td className="mono">
                            {sh.date ? formatDate(sh.date) : "—"}
                          </td>
                          <td>
                            <div className="shpg-acc-cell">
                              <span className="shpg-acc-name">
                                {sh.account?.accountName || "N/A"}
                              </span>
                              {sh.account?.accountNo && (
                                <span className="shpg-acc-no">
                                  {sh.account.accountNo}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="shpg-till-name">
                            {sh.tillCategory?.name || "N/A"}
                          </td>
                          <td>
                            <StatusChip status={sh.c2bStatus} />
                          </td>
                          <td className="r">{fmtMoney(sh.targetCash)}</td>
                          <td className="r">{fmtMoney(sh.declaredCash)}</td>
                          <td className="r">
                            <SignedVal value={sh.tillVariance} />
                          </td>
                          <td className="r">
                            {typeof sh.tillError === "number" &&
                            Math.abs(sh.tillError) > 0.005 ? (
                              <span className="shpg-val-neg">
                                {fmtMoneySigned(sh.tillError)}
                              </span>
                            ) : (
                              <span className="shpg-val-zero">0.00</span>
                            )}
                          </td>
                          <td className="r">{fmtMoney(sh.expectedC2B)}</td>
                          <td className="r">
                            {sh.actualC2B === null ||
                            sh.actualC2B === undefined ? (
                              <span className="shpg-val-pending">Pending</span>
                            ) : (
                              fmtMoney(sh.actualC2B)
                            )}
                          </td>
                          <td className="r">
                            <SignedVal value={sh.c2bVariance} />
                          </td>
                          <td>
                            <div className="shpg-row-actions">
                              <button
                                type="button"
                                className="shpg-icon-btn"
                                onClick={(e) => openRowActions(e, sh)}
                                aria-label="Row actions"
                              >
                                <IconDots />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="shpg-pagination-wrap">
                <MyPaginationComponent
                  page={page}
                  rowsPerPage={limit}
                  totalCount={totalCount}
                  onPageChange={(newPage) => updateUrl({ page: newPage })}
                  onRowsPerPageChange={(newRows) =>
                    updateUrl({ limit: parseInt(newRows, 10), page: 1 })
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Row actions menu — MUI Menu keeps keyboard nav for free */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={closeRowActions}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            if (actionMenuShift) handleRowClick(actionMenuShift._id);
            closeRowActions();
          }}
        >
          View details
        </MenuItem>
        {actionMenuShift?.c2bStatus?.toLowerCase() !== "checked" && (
          <MenuItem
            onClick={() => {
              if (actionMenuShift) handleEditShift(actionMenuShift);
              closeRowActions();
            }}
          >
            Edit shift
          </MenuItem>
        )}
        {actionMenuShift?.c2bStatus?.toLowerCase() === "checked" &&
          (["admin", "accountant"].includes(userRole.toLowerCase()) ? (
            <MenuItem
              onClick={() => {
                handleOpenRevertModal(actionMenuShift);
                closeRowActions();
              }}
            >
              Revert to Pending
            </MenuItem>
          ) : (
            <MenuItem
              disabled
              onClick={(e) => {
                e.stopPropagation();
                setSnackbar({
                  open: true,
                  message: "Contact Admin to Revert",
                  severity: "warning",
                });
                closeRowActions();
              }}
            >
              Contact Admin to Revert
            </MenuItem>
          ))}
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ================================================ */}
      {/* ADD SHIFT DIALOG                                  */}
      {/* ================================================ */}
      <Dialog
        open={openAddShift}
        onClose={handleCloseAddShift}
        fullWidth
        maxWidth="lg"
        className="shpg-dlg"
        PaperProps={{ className: "shpg-root" }}
      >
        <style>{SHIFTS_PAGE_CSS}</style>
        <div className="shpg-dlg-head">
          <div>
            <div className="shpg-dlg-title">Add Shift</div>
            <div className="shpg-dlg-sub">
              Record a till session. Live values update on the right as you type
              — they're what gets saved.
            </div>
          </div>
          <button
            className="shpg-dlg-close"
            onClick={handleCloseAddShift}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent dividers className="shpg-dlg-body shpg-no-pad">
          <div className="shpg-shift-body">
            <div className="shpg-shift-form">
              {/* A. Basic info */}
              <section className="shpg-fsec">
                <div className="shpg-fsec-head">
                  <span className="shpg-fsec-head-num">A</span>
                  <span>Basic info</span>
                  <span className="shpg-fsec-hint">Who, what till, what day</span>
                </div>
                <div className="shpg-frow shpg-frow-3">
                  <div>
                    <label className="shpg-flab shpg-flab-req">Account</label>
                    <Autocomplete
                      className="shpg-mui-ac"
                      size="small"
                      options={accounts}
                      getOptionLabel={(o) =>
                        o ? `${o.accountName} (${o.accountNo})` : ""
                      }
                      value={accountOptionByValue(shiftForm.account)}
                      onChange={(_e, v) =>
                        setShiftForm((p) => ({
                          ...p,
                          account: v ? v._id : "",
                        }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Pick an account…" />
                      )}
                    />
                  </div>
                  <div>
                    <label className="shpg-flab shpg-flab-req">Date</label>
                    <input
                      className="shpg-finput shpg-finput-mono"
                      type="date"
                      value={shiftForm.date}
                      onChange={(e) =>
                        setShiftForm((p) => ({ ...p, date: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="shpg-flab shpg-flab-req">
                      Till category
                    </label>
                    <Autocomplete
                      className="shpg-mui-ac"
                      size="small"
                      options={c2bCategories}
                      getOptionLabel={(o) => (o ? o.name : "")}
                      value={tillOptionByValue(shiftForm.tillCategory)}
                      onChange={(_e, v) =>
                        setShiftForm((p) => ({
                          ...p,
                          tillCategory: v ? v._id : "",
                        }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Pick a till…" />
                      )}
                    />
                  </div>
                </div>
              </section>

              {/* B. Cash flow */}
              <section className="shpg-fsec">
                <div className="shpg-fsec-head">
                  <span className="shpg-fsec-head-num">B</span>
                  <span>Cash flow</span>
                  <span className="shpg-fsec-hint">
                    Float at open, sales during, wages out
                  </span>
                </div>
                <div className="shpg-frow shpg-frow-3">
                  <div>
                    <label className="shpg-flab shpg-flab-req">
                      Starting float
                    </label>
                    <div className="shpg-money-input">
                      <input
                        type="number"
                        step="0.01"
                        {...disableScrollProps}
                        value={shiftForm.startingFloat}
                        onChange={(e) =>
                          setShiftForm((p) => ({
                            ...p,
                            startingFloat: e.target.value,
                          }))
                        }
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="shpg-flab shpg-flab-req">Cash sales</label>
                    <div className="shpg-money-input">
                      <input
                        type="number"
                        step="0.01"
                        {...disableScrollProps}
                        value={shiftForm.cashSales}
                        onChange={(e) =>
                          setShiftForm((p) => ({
                            ...p,
                            cashSales: e.target.value,
                          }))
                        }
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="shpg-flab">Drivers wage</label>
                    <div className="shpg-money-input">
                      <input
                        type="number"
                        step="0.01"
                        {...disableScrollProps}
                        value={shiftForm.driversWage}
                        onChange={(e) =>
                          setShiftForm((p) => ({
                            ...p,
                            driversWage: e.target.value,
                          }))
                        }
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* C. Cash in */}
              <section className="shpg-fsec">
                <div className="shpg-fsec-head">
                  <span className="shpg-fsec-head-num">C</span>
                  <span>Cash in</span>
                  <span className="shpg-fsec-hint">
                    Top-ups, change runs, ad-hoc additions
                  </span>
                </div>
                <div className="shpg-lines">
                  {shiftForm.cashInLines.map((line, i) => (
                    <div className="shpg-line" key={i}>
                      <div className="shpg-money-input">
                        <input
                          type="number"
                          step="0.01"
                          {...disableScrollProps}
                          value={line.amount}
                          onChange={(e) =>
                            handleCashInLineChange(i, "amount", e.target.value)
                          }
                          placeholder="0.00"
                        />
                      </div>
                      <input
                        className="shpg-finput"
                        placeholder="Description (e.g. Change run from safe)"
                        value={line.description}
                        onChange={(e) =>
                          handleCashInLineChange(
                            i,
                            "description",
                            e.target.value
                          )
                        }
                      />
                      {shiftForm.cashInLines.length > 1 && (
                        <button
                          type="button"
                          className="shpg-line-rm"
                          onClick={() => handleRemoveCashInLine(i)}
                          aria-label="Remove line"
                        >
                          <IconClose size={11} />
                        </button>
                      )}
                      {shiftForm.cashInLines.length === 1 && <span />}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="shpg-line-add"
                    onClick={handleAddCashInLine}
                  >
                    <IconPlus size={11} /> Add cash-in line
                  </button>
                  <div className="shpg-lines-total">
                    Cash in total <b>£{fmtMoney(calcVals.cashInTotal)}</b>
                  </div>
                </div>
              </section>

              {/* D. Cash out */}
              <section className="shpg-fsec">
                <div className="shpg-fsec-head">
                  <span className="shpg-fsec-head-num">D</span>
                  <span>Cash out</span>
                  <span className="shpg-fsec-hint">
                    Petty cash purchases, refunds
                  </span>
                </div>
                <div className="shpg-lines">
                  {shiftForm.cashOutLines.map((line, i) => (
                    <div className="shpg-line" key={i}>
                      <div className="shpg-money-input">
                        <input
                          type="number"
                          step="0.01"
                          {...disableScrollProps}
                          value={line.amount}
                          onChange={(e) =>
                            handleCashOutLineChange(i, "amount", e.target.value)
                          }
                          placeholder="0.00"
                        />
                      </div>
                      <input
                        className="shpg-finput"
                        placeholder="Description (e.g. Milk run — Costcutter)"
                        value={line.description}
                        onChange={(e) =>
                          handleCashOutLineChange(
                            i,
                            "description",
                            e.target.value
                          )
                        }
                      />
                      {shiftForm.cashOutLines.length > 1 && (
                        <button
                          type="button"
                          className="shpg-line-rm"
                          onClick={() => handleRemoveCashOutLine(i)}
                          aria-label="Remove line"
                        >
                          <IconClose size={11} />
                        </button>
                      )}
                      {shiftForm.cashOutLines.length === 1 && <span />}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="shpg-line-add"
                    onClick={handleAddCashOutLine}
                  >
                    <IconPlus size={11} /> Add cash-out line
                  </button>
                  <div className="shpg-lines-total">
                    Cash out total <b>£{fmtMoney(calcVals.cashOutTotal)}</b>
                  </div>
                </div>
              </section>

              {/* E. Till count */}
              <section className="shpg-fsec">
                <div className="shpg-fsec-head">
                  <span className="shpg-fsec-head-num">E</span>
                  <span>Till count</span>
                  <span className="shpg-fsec-hint">
                    What the system expects vs. what was counted
                  </span>
                </div>
                <div className="shpg-frow shpg-frow-2">
                  <div>
                    <label className="shpg-flab shpg-flab-req">
                      Target cash
                    </label>
                    <div className="shpg-money-input">
                      <input
                        type="number"
                        step="0.01"
                        {...disableScrollProps}
                        value={shiftForm.targetCash}
                        onChange={(e) =>
                          setShiftForm((p) => ({
                            ...p,
                            targetCash: e.target.value,
                          }))
                        }
                        placeholder="0.00"
                      />
                    </div>
                    <div className="shpg-fhint">What the system expected</div>
                  </div>
                  <div>
                    <label className="shpg-flab shpg-flab-req">
                      Declared cash
                    </label>
                    <div className="shpg-money-input">
                      <input
                        type="number"
                        step="0.01"
                        {...disableScrollProps}
                        value={shiftForm.declaredCash}
                        readOnly={shiftCountLines !== null}
                        onChange={(e) =>
                          setShiftForm((p) => ({
                            ...p,
                            declaredCash: e.target.value,
                          }))
                        }
                        placeholder="0.00"
                      />
                    </div>
                    <div className="shpg-fhint">
                      {shiftCountLines !== null ? (
                        <>
                          Locked to the counted total ·{" "}
                          <button
                            type="button"
                            className="shpg-link-btn"
                            onClick={() => setShiftCountLines(null)}
                          >
                            switch to direct entry
                          </button>
                        </>
                      ) : (
                        <>
                          What the cashier counted ·{" "}
                          <button
                            type="button"
                            className="shpg-link-btn"
                            onClick={() =>
                              setShiftCountLines(makeDefaultDenominationLines())
                            }
                          >
                            count cash instead
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {shiftCountLines !== null && (
                  <div style={{ marginTop: 12 }}>
                    <DenominationCounter
                      lines={shiftCountLines}
                      onQuantityChange={handleAddCountChange}
                      currency={userCurrency}
                      disabled={false}
                      totalValue={Number(shiftForm.declaredCash || 0)}
                    />
                  </div>
                )}
              </section>

              {/* F. People */}
              <section className="shpg-fsec">
                <div className="shpg-fsec-head">
                  <span className="shpg-fsec-head-num">F</span>
                  <span>People</span>
                  <span className="shpg-fsec-hint">
                    Cashiers and shift manager
                  </span>
                </div>
                <div className="shpg-frow shpg-frow-2">
                  <div>
                    <label className="shpg-flab">Till users</label>
                    <Autocomplete
                      className="shpg-mui-ac"
                      multiple
                      size="small"
                      options={tillUsers}
                      getOptionLabel={(o) => o.name}
                      filterSelectedOptions
                      value={tillUsers.filter((tu) =>
                        shiftForm.tillUsers.includes(tu._id)
                      )}
                      onChange={(_e, v) =>
                        setShiftForm((p) => ({
                          ...p,
                          tillUsers: v.map((x) => x._id),
                        }))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select till users"
                        />
                      )}
                    />
                  </div>
                  <div>
                    <label className="shpg-flab">Shift manager</label>
                    <Autocomplete
                      className="shpg-mui-ac"
                      size="small"
                      options={allUsers.filter((u) =>
                        ["Admin", "Manager", "Accountant"].includes(u.role)
                      )}
                      getOptionLabel={(o) => o.displayName || o.username}
                      value={userOptionByValue(shiftForm.shiftManager)}
                      onChange={(_e, v) =>
                        setShiftForm((p) => ({
                          ...p,
                          shiftManager: v ? v._id : "",
                        }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Pick a manager" />
                      )}
                    />
                  </div>
                </div>
              </section>

              {/* G. Notes */}
              <section className="shpg-fsec">
                <div className="shpg-fsec-head">
                  <span className="shpg-fsec-head-num">G</span>
                  <span>Notes</span>
                  <span className="shpg-fsec-hint">
                    Anything unusual about this session
                  </span>
                </div>
                <textarea
                  className="shpg-ftextarea"
                  placeholder="Optional — e.g. POS rebooted twice during service…"
                  value={shiftForm.tillNotes}
                  onChange={(e) =>
                    setShiftForm((p) => ({ ...p, tillNotes: e.target.value }))
                  }
                />
              </section>
            </div>

            {/* Live calculations rail */}
            <aside className="shpg-shift-side" aria-label="Live calculations">
              <div className="shpg-shift-side-head">
                <span>Live calculations</span>
                <span className="shpg-shift-side-pill">updates as you type</span>
              </div>

              <div className="shpg-calc">
                <div className="shpg-calc-row">
                  <span className="shpg-calc-lab">Cash in total</span>
                  <span className="shpg-calc-val">
                    £{fmtMoney(calcVals.cashInTotal)}
                  </span>
                </div>
                <div className="shpg-calc-row">
                  <span className="shpg-calc-lab">Cash out total</span>
                  <span className="shpg-calc-val">
                    £{fmtMoney(calcVals.cashOutTotal)}
                  </span>
                </div>
                <div className="shpg-calc-row is-feature">
                  <span className="shpg-calc-lab">Till variance</span>
                  <span
                    className={`shpg-calc-val ${
                      calcVals.tillVariance > 0.005
                        ? "is-pos"
                        : calcVals.tillVariance < -0.005
                        ? "is-neg"
                        : ""
                    }`}
                  >
                    {fmtMoneySigned(calcVals.tillVariance)}
                  </span>
                  <div className="shpg-calc-formula">declared − target</div>
                </div>
                <div className="shpg-calc-row is-feature">
                  <span className="shpg-calc-lab">Till error</span>
                  <span
                    className={`shpg-calc-val ${
                      Math.abs(calcVals.tillError) > 0.005 ? "is-neg" : ""
                    }`}
                  >
                    {fmtMoneySigned(calcVals.tillError)}
                  </span>
                  <div className="shpg-calc-formula">
                    target − (float + sales + in − out − wage)
                  </div>
                </div>
                <div className="shpg-calc-row is-feature">
                  <span className="shpg-calc-lab">Expected C2B</span>
                  <span className="shpg-calc-val">
                    £{fmtMoney(calcVals.expectedC2B)}
                  </span>
                  <div className="shpg-calc-formula">
                    max(0, declared − float)
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "auto",
                  fontSize: 11,
                  color: "var(--shpg-text-3)",
                  lineHeight: 1.5,
                  paddingTop: 12,
                  borderTop: "1px solid var(--shpg-border-1)",
                }}
              >
                <b
                  style={{
                    color: "var(--shpg-text-2)",
                    fontWeight: 600,
                  }}
                >
                  Tip:
                </b>{" "}
                a non-zero <b style={{ color: "var(--shpg-text-2)" }}>till error</b>{" "}
                means the cash-flow math doesn't add up — double-check before
                saving.
              </div>
            </aside>
          </div>
        </DialogContent>
        <DialogActions className="shpg-dlg-foot">
          <div className="shpg-dlg-foot-l">
            Fields marked{" "}
            <span style={{ color: "var(--shpg-primary)", fontWeight: 700 }}>*</span>{" "}
            are required
          </div>
          <div className="shpg-dlg-foot-r">
            <button
              type="button"
              className="shpg-btn shpg-btn-ghost"
              onClick={handleCloseAddShift}
              disabled={creatingShift}
            >
              Cancel
            </button>
            <button
              type="button"
              className="shpg-btn shpg-btn-primary"
              onClick={handleCreateShift}
              disabled={creatingShift}
            >
              {creatingShift && <span className="shpg-spinner" />}
              {creatingShift ? "Saving…" : "Save shift"}
            </button>
          </div>
        </DialogActions>
      </Dialog>

      {/* ================================================ */}
      {/* EDIT SHIFT DIALOG                                 */}
      {/* ================================================ */}
      {openEdit && editShiftData && (
        <Dialog
          open={openEdit}
          onClose={handleCloseEdit}
          fullWidth
          maxWidth="lg"
          className="shpg-dlg"
          PaperProps={{ className: "shpg-root" }}
        >
          <style>{SHIFTS_PAGE_CSS}</style>
          <div className="shpg-dlg-head">
            <div>
              <div className="shpg-dlg-title">Edit Shift</div>
              <div className="shpg-dlg-sub">
                Update the till session and re-save. Live values update as you
                type.
              </div>
            </div>
            <button
              className="shpg-dlg-close"
              onClick={handleCloseEdit}
              aria-label="Close"
            >
              <IconClose />
            </button>
          </div>
          <DialogContent dividers className="shpg-dlg-body shpg-no-pad">
            <div className="shpg-shift-body">
              <div className="shpg-shift-form">
                {/* A. Basic info */}
                <section className="shpg-fsec">
                  <div className="shpg-fsec-head">
                    <span className="shpg-fsec-head-num">A</span>
                    <span>Basic info</span>
                  </div>
                  <div className="shpg-frow shpg-frow-3">
                    <div>
                      <label className="shpg-flab">Account</label>
                      <input
                        className="shpg-finput"
                        readOnly
                        value={
                          editShiftData.account?.accountName
                            ? `${editShiftData.account.accountName} (${editShiftData.account.accountNo})`
                            : ""
                        }
                      />
                    </div>
                    <div>
                      <label className="shpg-flab shpg-flab-req">Date</label>
                      <input
                        className="shpg-finput shpg-finput-mono"
                        type="date"
                        name="date"
                        value={
                          editShiftData.date
                            ? editShiftData.date.split("T")[0]
                            : ""
                        }
                        onChange={handleEditChange}
                      />
                    </div>
                    <div>
                      <label className="shpg-flab shpg-flab-req">
                        Till category
                      </label>
                      <Autocomplete
                        className="shpg-mui-ac"
                        size="small"
                        options={c2bCategories}
                        getOptionLabel={(o) => (o ? o.name : "")}
                        value={tillOptionByValue(editShiftData.tillCategory)}
                        onChange={(_e, v) =>
                          handleEditChange({
                            name: "tillCategory",
                            value: v ? v._id : "",
                          })
                        }
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Pick a till…" />
                        )}
                      />
                    </div>
                  </div>
                </section>

                {/* B. Cash flow */}
                <section className="shpg-fsec">
                  <div className="shpg-fsec-head">
                    <span className="shpg-fsec-head-num">B</span>
                    <span>Cash flow</span>
                  </div>
                  <div className="shpg-frow shpg-frow-3">
                    <div>
                      <label className="shpg-flab shpg-flab-req">
                        Starting float
                      </label>
                      <div className="shpg-money-input">
                        <input
                          type="number"
                          step="0.01"
                          {...disableScrollProps}
                          name="startingFloat"
                          value={editShiftData.startingFloat}
                          onChange={handleEditChange}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="shpg-flab shpg-flab-req">
                        Cash sales
                      </label>
                      <div className="shpg-money-input">
                        <input
                          type="number"
                          step="0.01"
                          {...disableScrollProps}
                          name="cashSales"
                          value={editShiftData.cashSales}
                          onChange={handleEditChange}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="shpg-flab">Drivers wage</label>
                      <div className="shpg-money-input">
                        <input
                          type="number"
                          step="0.01"
                          {...disableScrollProps}
                          name="driversWage"
                          value={editShiftData.driversWage}
                          onChange={handleEditChange}
                        />
                      </div>
                    </div>
                  </div>
                </section>

                {/* C. Cash in */}
                <section className="shpg-fsec">
                  <div className="shpg-fsec-head">
                    <span className="shpg-fsec-head-num">C</span>
                    <span>Cash in</span>
                  </div>
                  <div className="shpg-lines">
                    {(editShiftData.cashInLines || []).map((line, i) => (
                      <div className="shpg-line" key={i}>
                        <div className="shpg-money-input">
                          <input
                            type="number"
                            step="0.01"
                            {...disableScrollProps}
                            value={line.amount}
                            onChange={(e) =>
                              handleEditCashInLineChange(
                                i,
                                "amount",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <input
                          className="shpg-finput"
                          placeholder="Description"
                          value={line.description}
                          onChange={(e) =>
                            handleEditCashInLineChange(
                              i,
                              "description",
                              e.target.value
                            )
                          }
                        />
                        {(editShiftData.cashInLines || []).length > 1 && (
                          <button
                            type="button"
                            className="shpg-line-rm"
                            onClick={() => handleEditRemoveCashInLine(i)}
                            aria-label="Remove line"
                          >
                            <IconClose size={11} />
                          </button>
                        )}
                        {(editShiftData.cashInLines || []).length === 1 && (
                          <span />
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="shpg-line-add"
                      onClick={handleEditAddCashInLine}
                    >
                      <IconPlus size={11} /> Add cash-in line
                    </button>
                    <div className="shpg-lines-total">
                      Cash in total{" "}
                      <b>£{fmtMoney(editCalcVals.cashInTotal)}</b>
                    </div>
                  </div>
                </section>

                {/* D. Cash out */}
                <section className="shpg-fsec">
                  <div className="shpg-fsec-head">
                    <span className="shpg-fsec-head-num">D</span>
                    <span>Cash out</span>
                  </div>
                  <div className="shpg-lines">
                    {(editShiftData.cashOutLines || []).map((line, i) => (
                      <div className="shpg-line" key={i}>
                        <div className="shpg-money-input">
                          <input
                            type="number"
                            step="0.01"
                            {...disableScrollProps}
                            value={line.amount}
                            onChange={(e) =>
                              handleEditCashOutLineChange(
                                i,
                                "amount",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <input
                          className="shpg-finput"
                          placeholder="Description"
                          value={line.description}
                          onChange={(e) =>
                            handleEditCashOutLineChange(
                              i,
                              "description",
                              e.target.value
                            )
                          }
                        />
                        {(editShiftData.cashOutLines || []).length > 1 && (
                          <button
                            type="button"
                            className="shpg-line-rm"
                            onClick={() => handleEditRemoveCashOutLine(i)}
                            aria-label="Remove line"
                          >
                            <IconClose size={11} />
                          </button>
                        )}
                        {(editShiftData.cashOutLines || []).length === 1 && (
                          <span />
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="shpg-line-add"
                      onClick={handleEditAddCashOutLine}
                    >
                      <IconPlus size={11} /> Add cash-out line
                    </button>
                    <div className="shpg-lines-total">
                      Cash out total{" "}
                      <b>£{fmtMoney(editCalcVals.cashOutTotal)}</b>
                    </div>
                  </div>
                </section>

                {/* E. Till count */}
                <section className="shpg-fsec">
                  <div className="shpg-fsec-head">
                    <span className="shpg-fsec-head-num">E</span>
                    <span>Till count</span>
                  </div>
                  <div className="shpg-frow shpg-frow-2">
                    <div>
                      <label className="shpg-flab shpg-flab-req">
                        Target cash
                      </label>
                      <div className="shpg-money-input">
                        <input
                          type="number"
                          step="0.01"
                          {...disableScrollProps}
                          name="targetCash"
                          value={editShiftData.targetCash}
                          onChange={handleEditChange}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="shpg-flab shpg-flab-req">
                        Declared cash
                      </label>
                      <div className="shpg-money-input">
                        <input
                          type="number"
                          step="0.01"
                          {...disableScrollProps}
                          name="declaredCash"
                          value={editShiftData.declaredCash}
                          readOnly={editCountLines !== null}
                          onChange={handleEditChange}
                        />
                      </div>
                      <div className="shpg-fhint">
                        {editCountLines !== null ? (
                          <>
                            Locked to the counted total ·{" "}
                            <button
                              type="button"
                              className="shpg-link-btn"
                              onClick={() => setEditCountLines(null)}
                            >
                              switch to direct entry
                            </button>
                          </>
                        ) : (
                          <>
                            What the cashier counted ·{" "}
                            <button
                              type="button"
                              className="shpg-link-btn"
                              onClick={() =>
                                setEditCountLines(
                                  makeDefaultDenominationLines()
                                )
                              }
                            >
                              count cash instead
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {editCountLines !== null && (
                    <div style={{ marginTop: 12 }}>
                      <DenominationCounter
                        lines={editCountLines}
                        onQuantityChange={handleEditCountChange}
                        currency={userCurrency}
                        disabled={false}
                        totalValue={Number(editShiftData.declaredCash || 0)}
                      />
                    </div>
                  )}
                </section>

                {/* F. People */}
                <section className="shpg-fsec">
                  <div className="shpg-fsec-head">
                    <span className="shpg-fsec-head-num">F</span>
                    <span>People</span>
                  </div>
                  <div className="shpg-frow shpg-frow-2">
                    <div>
                      <label className="shpg-flab">Till users</label>
                      <Autocomplete
                        className="shpg-mui-ac"
                        multiple
                        size="small"
                        options={tillUsers}
                        getOptionLabel={(o) => o.name}
                        filterSelectedOptions
                        value={tillUsers.filter((tu) =>
                          (editShiftData.tillUsers || []).includes(tu._id)
                        )}
                        onChange={(_e, v) =>
                          setEditShiftData((p) => ({
                            ...p,
                            tillUsers: v.map((x) => x._id),
                          }))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select till users"
                          />
                        )}
                      />
                    </div>
                    <div>
                      <label className="shpg-flab">Shift manager</label>
                      <Autocomplete
                        className="shpg-mui-ac"
                        size="small"
                        options={allUsers.filter((u) =>
                          ["Admin", "Manager", "Accountant"].includes(u.role)
                        )}
                        getOptionLabel={(o) => o.displayName || o.username}
                        value={userOptionByValue(editShiftData.shiftManager)}
                        onChange={(_e, v) =>
                          setEditShiftData((p) => ({
                            ...p,
                            shiftManager: v ? v._id : "",
                          }))
                        }
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Pick a manager" />
                        )}
                      />
                    </div>
                  </div>
                </section>

                {/* G. Notes */}
                <section className="shpg-fsec">
                  <div className="shpg-fsec-head">
                    <span className="shpg-fsec-head-num">G</span>
                    <span>Notes</span>
                  </div>
                  <textarea
                    className="shpg-ftextarea"
                    name="tillNotes"
                    placeholder="Optional"
                    value={editShiftData.tillNotes || ""}
                    onChange={handleEditChange}
                  />
                </section>
              </div>

              {/* Live calc rail */}
              <aside
                className="shpg-shift-side"
                aria-label="Live calculations"
              >
                <div className="shpg-shift-side-head">
                  <span>Live calculations</span>
                  <span className="shpg-shift-side-pill">
                    updates as you type
                  </span>
                </div>
                <div className="shpg-calc">
                  <div className="shpg-calc-row">
                    <span className="shpg-calc-lab">Cash in total</span>
                    <span className="shpg-calc-val">
                      £{fmtMoney(editCalcVals.cashInTotal)}
                    </span>
                  </div>
                  <div className="shpg-calc-row">
                    <span className="shpg-calc-lab">Cash out total</span>
                    <span className="shpg-calc-val">
                      £{fmtMoney(editCalcVals.cashOutTotal)}
                    </span>
                  </div>
                  <div className="shpg-calc-row is-feature">
                    <span className="shpg-calc-lab">Till variance</span>
                    <span
                      className={`shpg-calc-val ${
                        editCalcVals.tillVariance > 0.005
                          ? "is-pos"
                          : editCalcVals.tillVariance < -0.005
                          ? "is-neg"
                          : ""
                      }`}
                    >
                      {fmtMoneySigned(editCalcVals.tillVariance)}
                    </span>
                    <div className="shpg-calc-formula">declared − target</div>
                  </div>
                  <div className="shpg-calc-row is-feature">
                    <span className="shpg-calc-lab">Till error</span>
                    <span
                      className={`shpg-calc-val ${
                        Math.abs(editCalcVals.tillError) > 0.005 ? "is-neg" : ""
                      }`}
                    >
                      {fmtMoneySigned(editCalcVals.tillError)}
                    </span>
                    <div className="shpg-calc-formula">
                      target − (float + sales + in − out − wage)
                    </div>
                  </div>
                  <div className="shpg-calc-row is-feature">
                    <span className="shpg-calc-lab">Expected C2B</span>
                    <span className="shpg-calc-val">
                      £{fmtMoney(editCalcVals.expectedC2B)}
                    </span>
                    <div className="shpg-calc-formula">
                      max(0, declared − float)
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </DialogContent>
          <DialogActions className="shpg-dlg-foot">
            <div className="shpg-dlg-foot-l">
              Editing locks the shift while saving
            </div>
            <div className="shpg-dlg-foot-r">
              <button
                type="button"
                className="shpg-btn shpg-btn-ghost"
                onClick={handleCloseEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="shpg-btn shpg-btn-primary"
                onClick={handleSaveEdit}
              >
                Save changes
              </button>
            </div>
          </DialogActions>
        </Dialog>
      )}

      {/* ================================================ */}
      {/* ADD ACTUAL (FINALIZE) DIALOG                      */}
      {/* ================================================ */}
      <Dialog
        open={openFinalize}
        onClose={handleCloseFinalize}
        fullWidth
        maxWidth="lg"
        className="shpg-dlg"
        PaperProps={{ className: "shpg-root" }}
      >
        <style>{SHIFTS_PAGE_CSS}</style>
        <div className="shpg-dlg-head">
          <div>
            <div className="shpg-dlg-title">
              Add Actual C2B — Finalize Shifts
            </div>
            <div className="shpg-dlg-sub">
              Pick an account, then enter the actual deposit amount for each
              Pending shift. Finalizing turns them into Checked.
            </div>
          </div>
          <button
            className="shpg-dlg-close"
            onClick={handleCloseFinalize}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent dividers className="shpg-dlg-body">
          <div className="shpg-frow shpg-frow-2">
            <div>
              <label className="shpg-flab shpg-flab-req">Account</label>
              <Autocomplete
                className="shpg-mui-ac"
                size="small"
                options={accounts}
                getOptionLabel={(o) =>
                  o ? `${o.accountName} (${o.accountNo})` : ""
                }
                value={accountOptionByValue(finalizeAccount)}
                onChange={(_e, v) =>
                  handleFinalizeAccountChange(v ? v._id : "")
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Pick an account…" />
                )}
              />
              <div className="shpg-fhint">
                Which till's pending shifts to finalize
              </div>
            </div>
            <div>
              <label className="shpg-flab shpg-flab-req">
                Actual C2B date
              </label>
              <input
                ref={actualC2BDateRef}
                key={`flash-${dateFlashCount}`}
                className={`shpg-finput shpg-finput-mono ${
                  !actualC2BDate &&
                  finalizeAccount &&
                  pendingShifts.length > 0
                    ? `is-required-empty${
                        dateFlashCount > 0 ? " is-flashing" : ""
                      }`
                    : ""
                }`}
                type="date"
                value={actualC2BDate}
                onChange={(e) => setActualC2BDate(e.target.value)}
              />
              <div className="shpg-fhint">
                When the deposit went to the bank
              </div>
            </div>
          </div>

          {!finalizeAccount ? (
            <div className="shpg-state" style={{ padding: "32px 16px" }}>
              <div className="shpg-state-sub">
                Pick an account above to see its pending shifts.
              </div>
            </div>
          ) : pendingShifts.length === 0 ? (
            <div className="shpg-state" style={{ padding: "32px 16px" }}>
              <div className="shpg-state-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12l4 4 10-10"
                    stroke="var(--shpg-success)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="shpg-state-title">No pending shifts</div>
              <div className="shpg-state-sub">
                This account is all caught up.
              </div>
            </div>
          ) : (
            <>
              {!actualC2BDate && (
                <div className="shpg-required-banner" role="status">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path d="M8 1.5l7 12.5H1L8 1.5z" fill="#FFC107" stroke="#B86E00" strokeWidth="1" />
                    <path d="M8 6v4M8 11.5v.5" stroke="#5A3A00" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <span>
                    Please set the <b>Actual C2B date</b> above before selecting shifts and entering amounts.
                  </span>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 6,
                  fontSize: 11.5,
                  color: "var(--shpg-text-3)",
                }}
              >
                <span>
                  {pendingShifts.length} pending{" "}
                  {pendingShifts.length === 1 ? "shift" : "shifts"}
                </span>
                <span>Variance auto-calculates as you type</span>
              </div>
              <table className="shpg-pending-table">
                <thead>
                  <tr>
                    <th className="c-check">
                      <Tooltip
                        title={
                          !actualC2BDate
                            ? "Set the Actual C2B date above first, then tick shifts to enter amounts."
                            : ""
                        }
                        arrow
                        placement="right"
                      >
                        <span
                          className={`shpg-disabled-wrap ${actualC2BDate ? "is-active" : ""}`}
                          onClick={() => {
                            if (!actualC2BDate) focusActualC2BDate();
                          }}
                        >
                          <Checkbox
                            size="small"
                            disabled={!actualC2BDate}
                            checked={
                              pendingShifts.length > 0 &&
                              pendingShifts.every(
                                (s) => finalizeMap[s._id]?.checked
                              )
                            }
                            indeterminate={
                              pendingShifts.some(
                                (s) => finalizeMap[s._id]?.checked
                              ) &&
                              !pendingShifts.every(
                                (s) => finalizeMap[s._id]?.checked
                              )
                            }
                            onChange={handleToggleAllPending}
                          />
                        </span>
                      </Tooltip>
                    </th>
                    <th>Date</th>
                    <th className="r">Declared</th>
                    <th className="r">Expected C2B</th>
                    <th className="r">Actual C2B *</th>
                    <th className="r">Variance</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingShifts.map((sh) => {
                    const shiftId = sh._id;
                    const rec =
                      finalizeMap[shiftId] || {
                        checked: false,
                        actualC2B: "",
                        c2bNotes: "",
                      };
                    const rowVariance = rec.checked
                      ? getRowC2BVariance(shiftId, rec.actualC2B)
                      : null;
                    const vCls =
                      rowVariance === null
                        ? ""
                        : rowVariance > 0.005
                        ? "shpg-val-pos"
                        : rowVariance < -0.005
                        ? "shpg-val-neg"
                        : "shpg-val-zero";
                    return (
                      <tr key={shiftId} className={rec.checked ? "is-sel" : ""}>
                        <td className="c-check">
                          <Tooltip
                            title={
                              !actualC2BDate
                                ? "Set the Actual C2B date above first, then tick this row."
                                : ""
                            }
                            arrow
                            placement="right"
                          >
                            <span
                              className={`shpg-disabled-wrap ${actualC2BDate ? "is-active" : ""}`}
                              onClick={() => {
                                if (!actualC2BDate) focusActualC2BDate();
                              }}
                            >
                              <Checkbox
                                size="small"
                                disabled={!actualC2BDate}
                                checked={rec.checked}
                                onChange={(e) =>
                                  handleToggleShiftCheck(
                                    shiftId,
                                    e.target.checked
                                  )
                                }
                              />
                            </span>
                          </Tooltip>
                        </td>
                        <td>
                          <div
                            style={{
                              fontFamily: "Geist Mono, monospace",
                              fontSize: 12,
                            }}
                          >
                            {sh.date ? formatDate(sh.date) : "—"}
                          </div>
                          <div
                            style={{
                              fontSize: 10.5,
                              color: "var(--shpg-text-3)",
                            }}
                          >
                            {sh.tillCategory?.name || ""}
                          </div>
                        </td>
                        <td
                          className="r"
                          style={{
                            fontFamily: "Geist Mono, monospace",
                            fontVariantNumeric: "tabular-nums",
                            textAlign: "right",
                          }}
                        >
                          {fmtMoney(sh.declaredCash)}
                        </td>
                        <td
                          className="r"
                          style={{
                            fontFamily: "Geist Mono, monospace",
                            fontVariantNumeric: "tabular-nums",
                            textAlign: "right",
                          }}
                        >
                          {fmtMoney(sh.expectedC2B)}
                        </td>
                        <td style={{ width: 110 }}>
                          <Tooltip
                            title={
                              !actualC2BDate
                                ? "Set the Actual C2B date above first, then enter the amount."
                                : !rec.checked
                                ? "Tick the row first to enter an amount."
                                : ""
                            }
                            arrow
                            placement="top"
                          >
                            <span
                              style={{ display: "block" }}
                              onClick={() => {
                                if (!actualC2BDate) focusActualC2BDate();
                              }}
                            >
                              <input
                                type="number"
                                step="0.01"
                                {...disableScrollProps}
                                className="shpg-pending-mini-input"
                                disabled={!actualC2BDate || !rec.checked}
                                value={rec.actualC2B}
                                onChange={(e) =>
                                  handleActualC2BChange(
                                    shiftId,
                                    e.target.value
                                  )
                                }
                                placeholder="0.00"
                              />
                            </span>
                          </Tooltip>
                        </td>
                        <td
                          className={`r ${vCls}`}
                          style={{
                            fontFamily: "Geist Mono, monospace",
                            fontVariantNumeric: "tabular-nums",
                            textAlign: "right",
                            fontWeight: 600,
                          }}
                        >
                          {rec.checked && rowVariance !== null
                            ? fmtMoneySigned(rowVariance)
                            : "—"}
                        </td>
                        <td style={{ width: 180 }}>
                          <Tooltip
                            title={
                              !actualC2BDate
                                ? "Set the Actual C2B date above first."
                                : !rec.checked
                                ? "Tick the row first to add a note."
                                : ""
                            }
                            arrow
                            placement="top"
                          >
                            <span
                              style={{ display: "block" }}
                              onClick={() => {
                                if (!actualC2BDate) focusActualC2BDate();
                              }}
                            >
                              <input
                                type="text"
                                className="shpg-pending-mini-text"
                                disabled={!actualC2BDate || !rec.checked}
                                value={rec.c2bNotes}
                                onChange={(e) =>
                                  handleC2BNotesChange(shiftId, e.target.value)
                                }
                                placeholder="Optional note…"
                              />
                            </span>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="shpg-pending-totals">
                Total Actual C2B selected:{" "}
                <b>
                  £
                  {fmtMoney(
                    pendingShifts.reduce((total, shift) => {
                      const rec = finalizeMap[shift._id];
                      return rec && rec.checked
                        ? total + Number(rec.actualC2B || 0)
                        : total;
                    }, 0)
                  )}
                </b>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions className="shpg-dlg-foot">
          <div className="shpg-dlg-foot-l">
            {pendingShifts.length > 0 && (
              <>
                <b style={{ color: "var(--shpg-text-2)", fontWeight: 600 }}>
                  {selectedPendingCount}
                </b>{" "}
                of {pendingShifts.length} selected
              </>
            )}
          </div>
          <div className="shpg-dlg-foot-r">
            <button
              type="button"
              className="shpg-btn shpg-btn-ghost"
              onClick={handleCloseFinalize}
              disabled={isFinalizing}
            >
              Cancel
            </button>
            <Tooltip
              title={
                !actualC2BDate && pendingShifts.length > 0
                  ? "Set the Actual C2B date above first."
                  : ""
              }
              arrow
              placement="top"
            >
              <span
                onClick={() => {
                  if (!actualC2BDate && pendingShifts.length > 0)
                    focusActualC2BDate();
                }}
              >
                <button
                  type="button"
                  className="shpg-btn shpg-btn-success"
                  onClick={handleFinalizeSave}
                  disabled={
                    isFinalizing ||
                    selectedPendingCount === 0 ||
                    !actualC2BDate
                  }
                >
                  {isFinalizing && <span className="shpg-spinner" />}
                  {isFinalizing ? (
                    "Finalizing…"
                  ) : (
                    <>
                      Finalize{" "}
                      {selectedPendingCount > 0 && (
                        <span className="shpg-btn-count">
                          {selectedPendingCount}
                        </span>
                      )}
                    </>
                  )}
                </button>
              </span>
            </Tooltip>
          </div>
        </DialogActions>
      </Dialog>

      {/* ================================================ */}
      {/* VIEW DETAILS DIALOG                               */}
      {/* ================================================ */}
      <Dialog
        open={openDetail}
        onClose={handleCloseDetail}
        fullWidth
        maxWidth="md"
        className="shpg-dlg"
        PaperProps={{ className: "shpg-root" }}
      >
        <style>{SHIFTS_PAGE_CSS}</style>
        {selectedShift && (
          <>
            <div className="shpg-dlg-head">
              <div>
                <div className="shpg-dlg-title">
                  Shift detail · {selectedShift.account?.accountName || "N/A"}
                </div>
                <div className="shpg-dlg-sub">
                  {selectedShift.date ? formatDate(selectedShift.date) : "N/A"}{" "}
                  · {selectedShift.tillCategory?.name || "N/A"}
                </div>
              </div>
              <button
                className="shpg-dlg-close"
                onClick={handleCloseDetail}
                aria-label="Close"
              >
                <IconClose />
              </button>
            </div>
            <DialogContent dividers className="shpg-dlg-body">
              {/* Hero */}
              <div className="shpg-detail-hero">
                <div>
                  <div className="shpg-detail-hero-date">
                    {selectedShift.date
                      ? formatDate(selectedShift.date)
                      : "N/A"}
                  </div>
                  <div className="shpg-detail-hero-acc">
                    {selectedShift.account?.accountName || "N/A"}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--shpg-text-3)",
                    }}
                  >
                    {selectedShift.tillCategory?.name || "N/A"}
                  </div>
                </div>
                <div className="shpg-detail-hero-r">
                  <div className="shpg-detail-hero-lab">Declared cash</div>
                  <div className="shpg-detail-hero-val">
                    £{fmtMoney(selectedShift.declaredCash)}
                  </div>
                </div>
              </div>

              {selectedShift.tillNotes && (
                <div className="shpg-detail-sec">
                  <div className="shpg-detail-sec-head">
                    Notes from the floor
                  </div>
                  <div className="shpg-detail-notes">
                    {selectedShift.tillNotes}
                  </div>
                </div>
              )}

              {/* Financials */}
              <div className="shpg-detail-sec">
                <div className="shpg-detail-sec-head">Financials</div>
                <div className="shpg-detail-grid">
                  <div>
                    <div className="shpg-detail-field-lab">Starting float</div>
                    <div className="shpg-detail-field-val">
                      £{fmtMoney(selectedShift.startingFloat)}
                    </div>
                  </div>
                  <div>
                    <div className="shpg-detail-field-lab">Cash sales</div>
                    <div className="shpg-detail-field-val">
                      £{fmtMoney(selectedShift.cashSales)}
                    </div>
                  </div>
                  <div>
                    <div className="shpg-detail-field-lab">Driver wage</div>
                    <div
                      className={`shpg-detail-field-val ${
                        !selectedShift.driversWage ? "is-empty" : ""
                      }`}
                    >
                      {selectedShift.driversWage
                        ? `£${fmtMoney(selectedShift.driversWage)}`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="shpg-detail-field-lab">Target cash</div>
                    <div className="shpg-detail-field-val">
                      £{fmtMoney(selectedShift.targetCash)}
                    </div>
                  </div>
                  <div>
                    <div className="shpg-detail-field-lab">Declared cash</div>
                    <div className="shpg-detail-field-val">
                      £{fmtMoney(selectedShift.declaredCash)}
                    </div>
                  </div>
                  <div>
                    <div className="shpg-detail-field-lab">Till variance</div>
                    <div
                      className={`shpg-detail-field-val ${
                        selectedShift.tillVariance > 0.005
                          ? "is-pos"
                          : selectedShift.tillVariance < -0.005
                          ? "is-neg"
                          : ""
                      }`}
                    >
                      {fmtMoneySigned(selectedShift.tillVariance)}
                    </div>
                  </div>
                  <div>
                    <div className="shpg-detail-field-lab">Till error</div>
                    <div
                      className={`shpg-detail-field-val ${
                        Math.abs(selectedShift.tillError || 0) > 0.005
                          ? "is-neg"
                          : ""
                      }`}
                    >
                      {fmtMoneySigned(selectedShift.tillError)}
                    </div>
                  </div>
                  <div>
                    <div className="shpg-detail-field-lab">Expected C2B</div>
                    <div className="shpg-detail-field-val">
                      £{fmtMoney(selectedShift.expectedC2B)}
                    </div>
                  </div>
                  <div>
                    <div className="shpg-detail-field-lab">Actual C2B</div>
                    <div
                      className={`shpg-detail-field-val ${
                        selectedShift.actualC2B === null ||
                        selectedShift.actualC2B === undefined
                          ? "is-empty"
                          : ""
                      }`}
                    >
                      {selectedShift.actualC2B !== null &&
                      selectedShift.actualC2B !== undefined
                        ? `£${fmtMoney(selectedShift.actualC2B)}`
                        : "Pending"}
                    </div>
                  </div>
                  <div>
                    <div className="shpg-detail-field-lab">C2B variance</div>
                    <div
                      className={`shpg-detail-field-val ${
                        selectedShift.c2bVariance === null ||
                        selectedShift.c2bVariance === undefined
                          ? "is-empty"
                          : selectedShift.c2bVariance > 0.005
                          ? "is-pos"
                          : selectedShift.c2bVariance < -0.005
                          ? "is-neg"
                          : ""
                      }`}
                    >
                      {selectedShift.c2bVariance !== null &&
                      selectedShift.c2bVariance !== undefined
                        ? fmtMoneySigned(selectedShift.c2bVariance)
                        : "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Cash in / out lines */}
              <div className="shpg-detail-sec">
                <div className="shpg-detail-sec-head">Cash in lines</div>
                {(selectedShift.cashInLines || []).length === 0 ? (
                  <div className="shpg-detail-empty">
                    No cash-in lines recorded.
                  </div>
                ) : (
                  <div className="shpg-detail-lines">
                    {selectedShift.cashInLines.map((l, i) => (
                      <div className="shpg-detail-line" key={i}>
                        <span className="shpg-detail-line-desc">
                          {l.description || "—"}
                        </span>
                        <span className="shpg-detail-line-amt">
                          £{fmtMoney(l.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="shpg-detail-sec">
                <div className="shpg-detail-sec-head">Cash out lines</div>
                {(selectedShift.cashOutLines || []).length === 0 ? (
                  <div className="shpg-detail-empty">
                    No cash-out lines recorded.
                  </div>
                ) : (
                  <div className="shpg-detail-lines">
                    {selectedShift.cashOutLines.map((l, i) => (
                      <div className="shpg-detail-line" key={i}>
                        <span className="shpg-detail-line-desc">
                          {l.description || "—"}
                        </span>
                        <span className="shpg-detail-line-amt">
                          £{fmtMoney(l.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Declared cash breakdown (only when counted) */}
              {Array.isArray(selectedShift.declaredCashDenominations) &&
                selectedShift.declaredCashDenominations.length > 0 && (
                  <div className="shpg-detail-sec">
                    <div className="shpg-detail-sec-head">
                      Declared cash breakdown
                    </div>
                    <div className="shpg-detail-lines">
                      {[...selectedShift.declaredCashDenominations]
                        .sort((a, b) => b.denomination - a.denomination)
                        .map((l, i) => (
                          <div className="shpg-detail-line" key={i}>
                            <span className="shpg-detail-line-desc">
                              {l.quantity} × £{fmtMoney(l.denomination)}
                            </span>
                            <span className="shpg-detail-line-amt">
                              £{fmtMoney(l.denomination * l.quantity)}
                            </span>
                          </div>
                        ))}
                      <div className="shpg-detail-line">
                        <span
                          className="shpg-detail-line-desc"
                          style={{ fontWeight: 600 }}
                        >
                          Counted total
                        </span>
                        <span className="shpg-detail-line-amt">
                          £
                          {fmtMoney(
                            sumDenomLines(
                              selectedShift.declaredCashDenominations
                            )
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

              {/* People */}
              <div className="shpg-detail-sec">
                <div className="shpg-detail-sec-head">People</div>
                <div className="shpg-detail-grid shpg-detail-grid-2">
                  <div>
                    <div className="shpg-detail-field-lab">Till users</div>
                    <div className="shpg-detail-people">
                      {Array.isArray(selectedShift.tillUsers) &&
                      selectedShift.tillUsers.length > 0 ? (
                        selectedShift.tillUsers.map((u) => (
                          <span
                            key={u._id || u.name}
                            className="shpg-people-chip"
                          >
                            {u.name}
                          </span>
                        ))
                      ) : (
                        <span
                          className="shpg-detail-field-val is-text is-empty"
                          style={{ fontFamily: "Geist, sans-serif" }}
                        >
                          —
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="shpg-detail-field-lab">Shift manager</div>
                    <div className="shpg-detail-field-val is-text">
                      {selectedShift.shiftManager
                        ? selectedShift.shiftManager.displayName ||
                          selectedShift.shiftManager.username
                        : "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Meta */}
              <div className="shpg-detail-meta">
                <span>
                  Status:{" "}
                  <b>
                    <StatusChip status={selectedShift.c2bStatus} />
                  </b>
                </span>
                {selectedShift._id && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontFamily: "Geist Mono, monospace",
                    }}
                  >
                    {selectedShift._id}
                  </span>
                )}
              </div>
            </DialogContent>
            <DialogActions className="shpg-dlg-foot">
              <div className="shpg-dlg-foot-l">
                <StatusChip status={selectedShift.c2bStatus} />
              </div>
              <div className="shpg-dlg-foot-r">
                <button
                  type="button"
                  className="shpg-btn shpg-btn-ghost"
                  onClick={handleCloseDetail}
                >
                  Close
                </button>
                {selectedShift.c2bStatus?.toLowerCase() !== "checked" && (
                  <button
                    type="button"
                    className="shpg-btn shpg-btn-primary"
                    onClick={() => {
                      handleCloseDetail();
                      handleEditShift(selectedShift);
                    }}
                  >
                    Edit shift
                  </button>
                )}
              </div>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ================================================ */}
      {/* REVERT MODAL                                      */}
      {/* ================================================ */}
      <Dialog
        open={openRevertModal}
        onClose={handleCloseRevertModal}
        fullWidth
        maxWidth="sm"
        className="shpg-dlg"
        PaperProps={{ className: "shpg-root" }}
      >
        <style>{SHIFTS_PAGE_CSS}</style>
        <div className="shpg-dlg-head">
          <div>
            <div className="shpg-dlg-title">Revert shift to Pending?</div>
            <div className="shpg-dlg-sub">
              This will undo the C2B finalization.
            </div>
          </div>
          <button
            className="shpg-dlg-close"
            onClick={handleCloseRevertModal}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent dividers className="shpg-dlg-body">
          {selectedShiftForRevert && (
            <>
              <div className="shpg-revert-context">
                <div className="shpg-revert-context-l">
                  <span className="shpg-revert-context-lab">Shift</span>
                  <span className="shpg-revert-context-val">
                    {selectedShiftForRevert.date
                      ? formatDate(selectedShiftForRevert.date)
                      : "—"}{" "}
                    ·{" "}
                    {selectedShiftForRevert.account?.accountName || "N/A"}
                  </span>
                </div>
                <StatusChip status="Checked" />
              </div>

              <div className="shpg-revert-warning">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{ flexShrink: 0, marginTop: 1 }}
                  aria-hidden
                >
                  <path
                    d="M8 1.5l7 12.5H1L8 1.5z"
                    fill="#FFC107"
                    stroke="#B86E00"
                    strokeWidth="1"
                  />
                  <path
                    d="M8 6v4M8 11.5v.5"
                    stroke="#5A3A00"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <div className="shpg-revert-warning-text">
                  The <b>Actual C2B</b>{" "}
                  {selectedShiftForRevert.actualC2B !== null &&
                  selectedShiftForRevert.actualC2B !== undefined && (
                    <>
                      (£{fmtMoney(selectedShiftForRevert.actualC2B)})
                    </>
                  )}{" "}
                  and <b>C2B Variance</b>{" "}
                  {selectedShiftForRevert.c2bVariance !== null &&
                  selectedShiftForRevert.c2bVariance !== undefined && (
                    <>
                      ({fmtMoneySigned(selectedShiftForRevert.c2bVariance)})
                    </>
                  )}{" "}
                  will be cleared, and the shift will return to <b>Pending</b>{" "}
                  status.
                </div>
              </div>

              <label className="shpg-flab shpg-flab-req">
                Why are you reverting this shift?
              </label>
              <textarea
                className="shpg-ftextarea"
                placeholder="e.g. Deposit amount was entered incorrectly — bank statement shows £304.00 not £340.00"
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                autoFocus
                style={{ minHeight: 78 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions className="shpg-dlg-foot">
          <div className="shpg-dlg-foot-l">
            Restricted to Admin and Accountant roles
          </div>
          <div className="shpg-dlg-foot-r">
            <button
              type="button"
              className="shpg-btn shpg-btn-ghost"
              onClick={handleCloseRevertModal}
              disabled={reverting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="shpg-btn shpg-btn-warn"
              onClick={handleConfirmRevert}
              disabled={reverting || !revertReason.trim()}
            >
              {reverting && <span className="shpg-spinner" />}
              {reverting ? "Reverting…" : "Revert shift"}
            </button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
}
